;(function (global, document) {
  'use strict'

  const VERSION = '1.1.0'
  const GLOBAL_NAME = 'AccessibilityPreferenceWidget'
  const HOST_ATTRIBUTE = 'data-accessibility-preference-widget'
  const DEFAULT_CONFIG = {
    autoMount: true,
    disabled: false,
    excludePaths: [],
    includePaths: [],
    observeDom: true,
    position: 'bottom-right',
    offsetX: '25px',
    offsetY: '25px',
    zIndex: 999999,
    language: 'auto',
    nonce: '',
  }
  const localStorage = createSafeStorage()

  let activeConfig
  let controller = null
  let host = null
  let routeObserver = null
  let routeCheckScheduled = false
  let cancelScheduledRouteCheck = null
  let lastPath = getCurrentPath()

  function createSafeStorage() {
    const fallback = new Map()
    let nativeStorage = null
    try {
      nativeStorage = global.localStorage
      const probeKey = '__a11y_widget_storage_probe__'
      nativeStorage.setItem(probeKey, '1')
      nativeStorage.removeItem(probeKey)
    } catch {
      nativeStorage = null
    }

    return {
      get length() {
        return nativeStorage ? nativeStorage.length : fallback.size
      },
      key(index) {
        if (nativeStorage) return nativeStorage.key(index)
        return [...fallback.keys()][index] ?? null
      },
      getItem(key) {
        if (nativeStorage) return nativeStorage.getItem(key)
        return fallback.has(String(key)) ? fallback.get(String(key)) : null
      },
      setItem(key, value) {
        if (nativeStorage) nativeStorage.setItem(key, String(value))
        else fallback.set(String(key), String(value))
      },
      removeItem(key) {
        if (nativeStorage) nativeStorage.removeItem(key)
        else fallback.delete(String(key))
      },
      clear() {
        if (nativeStorage) nativeStorage.clear()
        else fallback.clear()
      },
    }
  }

  function parseBoolean(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback
    return !['false', '0', 'off', 'no'].includes(String(value).toLowerCase())
  }

  function parsePathList(value) {
    if (!value) return []
    if (Array.isArray(value)) return value
    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
  }

  function readScriptConfig() {
    const script = document.currentScript
    if (!script) return {}

    const { dataset } = script
    return {
      autoMount: parseBoolean(dataset.autoMount, undefined),
      disabled: parseBoolean(dataset.disabled, undefined),
      excludePaths: parsePathList(dataset.excludePaths),
      includePaths: parsePathList(dataset.includePaths),
      observeDom: parseBoolean(dataset.observeDom, undefined),
      position: dataset.position,
      offsetX: dataset.offsetX,
      offsetY: dataset.offsetY,
      zIndex: dataset.zIndex ? Number(dataset.zIndex) : undefined,
      language: dataset.language,
      nonce: dataset.nonce,
    }
  }

  function removeUndefined(object) {
    return Object.fromEntries(
      Object.entries(object).filter(([, value]) => value !== undefined),
    )
  }

  function normalizeConfig(options = {}) {
    const globalConfig = global.AccessibilityPreferenceWidgetConfig || {}
    const merged = {
      ...DEFAULT_CONFIG,
      ...removeUndefined(globalConfig),
      ...removeUndefined(readScriptConfig()),
      ...removeUndefined(options),
    }

    merged.excludePaths = parsePathList(merged.excludePaths)
    merged.includePaths = parsePathList(merged.includePaths)
    merged.position =
      merged.position === 'bottom-left' ? 'bottom-left' : 'bottom-right'
    merged.offsetX = String(merged.offsetX || DEFAULT_CONFIG.offsetX)
    merged.offsetY = String(merged.offsetY || DEFAULT_CONFIG.offsetY)
    merged.zIndex = Number.isFinite(Number(merged.zIndex))
      ? Number(merged.zIndex)
      : DEFAULT_CONFIG.zIndex
    merged.nonce = String(merged.nonce || '')
    return merged
  }

  function getCurrentPath() {
    return global.location
      ? `${global.location.pathname}${global.location.search}${global.location.hash}`
      : '/'
  }

  function matchesPath(path, matcher) {
    if (typeof matcher === 'function') return Boolean(matcher(path))
    if (matcher instanceof RegExp) return matcher.test(path)
    if (typeof matcher !== 'string' || matcher.length === 0) return false

    if (matcher.endsWith('*')) return path.startsWith(matcher.slice(0, -1))
    return path === matcher || path.startsWith(`${matcher.replace(/\/$/, '')}/`)
  }

  function shouldMount(config = activeConfig) {
    if (config.disabled) return false

    const path = getCurrentPath()
    if (
      config.includePaths.length > 0 &&
      !config.includePaths.some(matcher => matchesPath(path, matcher))
    ) {
      return false
    }

    return !config.excludePaths.some(matcher => matchesPath(path, matcher))
  }

  function emit(name, detail = {}) {
    if (typeof global.CustomEvent !== 'function') return
    document.dispatchEvent(
      new global.CustomEvent(`accessibility-preference-widget:${name}`, {
        detail: { version: VERSION, ...detail },
      }),
    )
  }

  function applyHostConfig(target, config) {
    target.dataset.position = config.position
    target.style.setProperty('--a11y-widget-offset-x', config.offsetX)
    target.style.setProperty('--a11y-widget-offset-y', config.offsetY)
    target.style.setProperty('--a11y-widget-z-index', String(config.zIndex))
  }

  function prepareWidgetStyle(style) {
    style.setAttribute('data-a11y-owned', '')
    if (activeConfig.nonce) style.setAttribute('nonce', activeConfig.nonce)
    return style
  }

  function mount(options = {}) {
    activeConfig = normalizeConfig({ ...activeConfig, ...options })

    if (!shouldMount()) {
      destroy()
      return null
    }

    if (!document.body) {
      document.addEventListener('DOMContentLoaded', () => mount(options), {
        once: true,
      })
      return null
    }

    if (controller && host && host.isConnected) {
      applyHostConfig(host, activeConfig)
      controller.configure(activeConfig)
      controller.refresh()
      return host
    }

    const existingHost = document.querySelector(`[${HOST_ATTRIBUTE}]`)
    if (existingHost) existingHost.remove()

    host = document.createElement('div')
    host.id = `accessibility-preference-widget-${Math.random()
      .toString(36)
      .slice(2, 8)}`
    host.setAttribute(HOST_ATTRIBUTE, '')
    host.setAttribute('data-version', VERSION)
    applyHostConfig(host, activeConfig)
    document.body.appendChild(host)

    const shadowRoot = host.attachShadow({ mode: 'open' })
    const style = prepareWidgetStyle(document.createElement('style'))
    style.textContent = `:host {
  --a11y-primary: rgb(20, 184, 166);
  --a11y-primary-hover: rgb(13, 148, 136);
  --a11y-primary-soft: rgba(20, 184, 166, 0.15);
  --a11y-text-main: rgb(19, 56, 73);
  --a11y-text-muted: rgb(91, 116, 128);
  --a11y-font-family:
    system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --a11y-widget-offset-x: 25px;
  --a11y-widget-offset-y: 25px;
  --a11y-widget-z-index: 999999;
  color-scheme: light;
}

#a11y-widget-panel,
#a11y-widget-trigger {
  --text-light: rgba(255, 255, 255, 0.95);
}

#a11y-widget-trigger,
#a11y-widget-panel,
#a11y-widget-panel *:not(script):not(style) {
  font-family: var(--a11y-font-family) !important;
}

* {
  box-sizing: border-box;
}

/* Tetikleyici Buton - Premium Glass Effect */
#a11y-widget-trigger {
  position: fixed;
  bottom: calc(
    var(--a11y-widget-offset-y) + env(safe-area-inset-bottom, 0px)
  );
  right: calc(
    var(--a11y-widget-offset-x) + env(safe-area-inset-right, 0px)
  );
  width: 56px;
  height: 56px;
  z-index: calc(var(--a11y-widget-z-index) - 1);

  /* Flex Ortalama */
  display: flex;
  align-items: center;
  justify-content: center;

  /* --- GLASS GÖRÜNÜMÜ --- */

  /* Arka Plan: Yarı saydam canlı teal gradyanı */
  background: linear-gradient(
    135deg,
    rgba(37, 76, 72, 0.74) 0%,
    rgba(14, 183, 168, 0.81) 100%
  );

  /* Bulanıklık Efekti (Arkasından geçen içeriği flulaştırır) */
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  /* Kenarlık: Üst sol köşeden ışık vuruyormuş gibi beyaz parlama */
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;

  /* Gölgeler: Hem derinlik hem de renkli 'glow' efekti */
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15),
    /* Genel derinlik gölgesi */ 0 4px 15px rgba(20, 184, 166, 0.4),
    /* Renkli parlama (Glow) */ inset 0 0 0 1px rgba(255, 255, 255, 0.15); /* İçten ince bir cam hattı */

  /* Etkileşim */
  cursor: pointer;
  /* Bouncy (yaylanan) bir geçiş efekti */
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

/* Hover (Üzerine Gelince) */
#a11y-widget-trigger:hover {
  /* Biraz büyüsün ve dönsün */
  transform: scale(1.1) rotate(15deg);

  /* Rengi biraz daha opaklaşsın */
  background: linear-gradient(
    135deg,
    rgba(37, 76, 72, 0.85) 0%,
    rgba(14, 183, 168, 0.9) 100%
  );

  /* Glow efekti güçlensin */
  box-shadow: 0 15px 35px rgba(20, 184, 166, 0.5),
    0 0 0 4px rgba(255, 255, 255, 0.2); /* Dışına ikinci bir halka (ring) */
}

/* İkon Rengi */
#a11y-widget-trigger svg {
  width: 28px;
  height: 28px;
  fill: #ffffff;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2)); /* İkona da hafif gölge */
  transition: transform 0.4s ease;
}

#a11y-widget-trigger:hover svg {
  transform: scale(1.1);
}

@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(20, 184, 166, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(20, 184, 166, 0);
  }
}

#a11y-widget-trigger {
  animation: pulse-glow 3s infinite;
}

/* Ana panel - Geliştirilmiş glass morphism */
#a11y-widget-panel {
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%) translateX(120%);
  width: min(520px, 100vw);
  max-height: 100dvh;
  height: 100dvh;
  /* background: rgba(245, 247, 250, 0.7); */
  /* backdrop-filter: blur(100px);
  -webkit-backdrop-filter: blur(100px); */
  border-top-left-radius: 24px;
  /* border-bottom-left-radius: 16px; */
  /* box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1); */
  /* box-shadow: 0 22px 110px rgba(180, 191, 208, 0.56); */
  z-index: var(--a11y-widget-z-index);
  opacity: 0;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  display: flex;
  /* border: 1px solid rgba(245, 247, 250, 0.2); */
  flex-direction: column;

  background: rgba(245, 247, 250, 0.7);
  backdrop-filter: blur(14px) saturate(120%);
  -webkit-backdrop-filter: blur(14px) saturate(120%);
  /* ince saydam sınır (glass look) */
  border: 1px solid rgba(255, 255, 255, 0.15);
  /* hafif gölge ile yükselti */
  box-shadow: 0 12px 30px rgba(10, 20, 30, 0.2);
}
/* isteğe bağlı: üstte hafif renk tonu (teal tint) */
#a11y-widget-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(13, 120, 110, 0.03),
    rgba(13, 120, 110, 0.05)
  );
}

#a11y-widget-panel.open {
  transform: translateY(-50%) translateX(0);
  opacity: 1;
}

:host([data-position='bottom-left']) #a11y-widget-trigger {
  right: auto;
  left: calc(
    var(--a11y-widget-offset-x) + env(safe-area-inset-left, 0px)
  );
}

:host([data-position='bottom-left']) #a11y-widget-panel {
  right: auto;
  left: 0;
  transform: translateY(-50%) translateX(-120%);
  border-radius: 0 24px 24px 0;
}

:host([data-position='bottom-left']) #a11y-widget-panel.open {
  transform: translateY(-50%) translateX(0);
}

/* Header - Geliştirilmiş */
.a11y-header {
  padding: 24px;
  /* background: rgba(245, 247, 250, 0.15); */
  /* border-bottom: 1px solid rgba(0, 0, 0, 0.05); */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.a11y-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--a11y-text-main);
  letter-spacing: -0.5px;
  font-family: var(--a11y-font-family) !important;
}

.a11y-header small {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--a11y-text-main);
  margin-top: 2px;
  font-family: var(--a11y-font-family) !important;
}

.a11y-close {
  background-color: rgba(255, 255, 255, 0.65);
  border: 2px solid transparent;
  color: var(--a11y-text-main);
  cursor: pointer;
  padding: 8px;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.a11y-close:hover {
  background-color: rgba(255, 255, 255, 0.8);
  border-color: var(--a11y-primary);
  color: var(--a11y-primary);
  box-shadow: 0 0 0 3px rgb(from var(--a11y-primary) r g b / 0.25);
}

.a11y-close svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.a11y-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  /* background: rgba(255, 255, 255, 0.1); */
  /* backdrop-filter: blur(15px); */
}

.a11y-content::-webkit-scrollbar {
  width: 6px;
}

.a11y-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.02);
}

.a11y-content::-webkit-scrollbar-thumb {
  background-color: rgba(20, 184, 166, 0.4);
  border-radius: 10px;
}

.a11y-content::-webkit-scrollbar-thumb:hover {
  background-color: var(--a11y-primary);
}

/* Kategori etiketleri - Geliştirilmiş */
.a11y-category {
  margin-bottom: 24px;
}

.category-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--a11y-text-muted);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 12px;
  padding-left: 4px;
  opacity: 0.8;
}

.a11y-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.a11y-grid-secondary {
  grid-template-columns: repeat(2, 1fr) !important;
}

/* BUTON KONTEYNIRI - Geliştirilmiş Tik İşareti için */
.button-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Geliştirilmiş Brutalist Butonlar */
.brutalist-button {
  width: 100%;
  aspect-ratio: 1 / 1;
  min-height: 110px;
  height: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px;
  /* background: rgba(255, 255, 255, 0.6); */
  /* border: 1px solid rgba(255, 255, 255, 0.6); */
  /* border: 1px solid rgba(20, 184, 166, 0.4); */
  border: 2px solid transparent;
  border-radius: 16px;
  /* box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); */
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--a11y-text-main);
  text-decoration: none;
  position: relative;
  /* backdrop-filter: blur(10px); */

  background-color: rgba(255, 255, 255, 0.65);
  /* border: 1px solid rgba(255, 255, 255, 0.4); */
}

.brutalist-button:hover {
  /* transform: translateY(-3px); */
  /* background: rgba(255, 255, 255, 0.8); */
  /* box-shadow: 0 8px 15px rgba(20, 184, 166, 0.1); */
  border-color: var(--a11y-primary);
}

.brutalist-button.active {
  /* background: var(--a11y-primary-soft); */
  border: 2px solid var(--a11y-primary);
  box-shadow: 0 0 0 5px rgb(from var(--a11y-primary) r g b / 0.25);
  background-color: rgba(255, 255, 255, 0.8);
}

/* İkon Stilleri */
.feature-icon {
  margin-bottom: 10px;
  color: var(--a11y-text-muted);
  transition: 0.2s;
}

.feature-icon svg {
  width: 28px;
  height: 28px;
  fill: currentColor;
}

.brutalist-button.active .feature-icon {
  color: var(--a11y-primary);
  transform: scale(1.1);
}

/* Metin Stilleri */
.button-text {
  line-height: 1.2;
}

.button-text span {
  font-size: 14px;
  font-weight: 700;
  color: var(--a11y-text-main);
}

.brutalist-button.active .button-text span {
  font-weight: 700;
  color: var(--a11y-primary);
}

/* YENİ: Geliştirilmiş Tik İşareti - Kullanıcının beğendiği stilde */
.a11y-card-check {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 22px;
  height: 22px;
  background: var(--a11y-primary);
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: scale(0);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Buton aktifse check göster */
.button-container:has(.brutalist-button.active) .a11y-card-check,
.button-container.active .a11y-card-check {
  opacity: 1;
  transform: scale(1);
}

/* Progress bars - Geliştirilmiş */
.progress-bars {
  display: flex;
  gap: 4px;
  margin-top: 8px;
  width: 70%;
  justify-content: center;
}

.bar {
  flex: 1;
  height: 4px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  transition: 0.3s;
}

.bar.active {
  background-color: var(--a11y-primary);
}

.a11y-footer {
  padding: 24px;
}

/* Reset butonu - Geliştirilmiş */
.a11y-reset {
  width: 100%;
  padding: 14px;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 12px;
  color: var(--a11y-text-main);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.65);
}

.a11y-reset:hover {
  border: 2px solid var(--a11y-primary);
  color: var(--a11y-primary);
  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 5px rgb(from var(--a11y-primary) r g b / 0.25);
}

/* Eski kart stilleri (backward compatibility için) */
.a11y-card {
  background: rgba(255, 255, 255, 0.5);
  /* backdrop-filter: blur(15px); */
  border-radius: 16px;
  padding: 12px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 110px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.a11y-card:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 15px rgba(20, 184, 166, 0.1);
  border-color: var(--a11y-primary);
}

.a11y-card.active {
  background: var(--a11y-primary-soft);
  border: 2px solid var(--a11y-primary);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5) inset;
}

.a11y-card-icon {
  width: 32px;
  height: 32px;
  /* margin-bottom: 12px; */
  fill: var(--a11y-text-muted);
  transition: fill 0.2s ease;
}

.a11y-card.active .a11y-card-icon {
  fill: var(--a11y-primary);
}

.a11y-card-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--a11y-text-main);
  margin: 0;
  font-family:
    system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
}

.a11y-card.active .a11y-card-title {
  font-weight: 700;
  color: var(--a11y-primary);
}

/* Eski check işareti (backward compatibility) */
.a11y-card-check.legacy {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: var(--a11y-primary);
  border-radius: 50%;
  display: none;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.a11y-card.active .a11y-card-check.legacy {
  display: flex;
}

/* Accessibility Features */
body.a11y-high-contrast {
  background: #000 !important;
  color: #fff !important;
}

body.a11y-high-contrast * {
  background-color: #000 !important;
  color: #fff !important;
  border-color: #fff !important;
}

/* Yüksek Kontrast Modları */
body.a11y-contrast-invert {
  filter: invert(1) hue-rotate(180deg);
}

/* Widget'ı invert'ten koru */
body.a11y-contrast-invert #a11y-widget-trigger,
body.a11y-contrast-invert #a11y-widget-panel,
body.a11y-contrast-invert #a11y-widget-panel * {
  filter: invert(1) hue-rotate(180deg);
}

body.a11y-contrast-dark {
  background: #000000 !important;
  color: #ffffff !important;
}

body.a11y-contrast-dark
  *:not(script):not(style):not(#a11y-widget-trigger):not(
    #a11y-widget-panel
  ):not(#a11y-widget-panel *) {
  background-color: #000000 !important;
  color: #ffffff !important;
  border-color: #ffffff !important;
}

body.a11y-contrast-warm {
  background: #2a0f0f !important;
  color: #ffeedd !important;
}

body.a11y-contrast-warm
  *:not(script):not(style):not(#a11y-widget-trigger):not(
    #a11y-widget-panel
  ):not(#a11y-widget-panel *) {
  background-color: #2a0f0f !important;
  color: #ffeedd !important;
  border-color: #ff9966 !important;
}

body.a11y-contrast-light {
  background: #ffffff !important;
  color: #000000 !important;
}

body.a11y-contrast-light
  *:not(script):not(style):not(#a11y-widget-trigger):not(
    #a11y-widget-panel
  ):not(#a11y-widget-panel *) {
  background-color: #ffffff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

body.a11y-contrast-blue {
  background: #000033 !important;
  color: #ffff00 !important;
}

body.a11y-contrast-blue
  *:not(script):not(style):not(#a11y-widget-trigger):not(
    #a11y-widget-panel
  ):not(#a11y-widget-panel *) {
  background-color: #000033 !important;
  color: #ffff00 !important;
  border-color: #00ffff !important;
}

/* Kontrast modları için ortak kurallar */
body[class*='a11y-contrast-'] a {
  text-decoration: underline !important;
}

body[class*='a11y-contrast-'] button {
  font-weight: bold !important;
}

body[class*='a11y-contrast-'] input,
body[class*='a11y-contrast-'] select,
body[class*='a11y-contrast-'] textarea {
  border-width: 2px !important;
}

body.a11y-pause-animations * {
  animation-play-state: paused !important;
  animation: none !important;
  transition: none !important;
}

body.a11y-highlight-links a {
  background: #ffeb3b !important;
  color: #000 !important;
  text-decoration: underline !important;
  padding: 2px 4px;
  border-radius: 2px;
}

body.a11y-large-cursor,
body.a11y-large-cursor * {
  cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M4 4 L4 24 L10 18 L14 28 L18 26 L14 16 L20 16 Z" fill="black" stroke="white" stroke-width="2"/></svg>')
      0 0,
    auto !important;
}

@media (max-width: 521px) {
  #a11y-widget-panel {
    width: 100%;
    transform: translateY(-50%) translateX(0) scale(0.9);
    opacity: 0;
    pointer-events: none;
    border-radius: 0;
  }

  #a11y-widget-panel.open {
    transform: translateY(-50%) translateX(0) scale(1);
    opacity: 1;
    pointer-events: all;
  }

  .a11y-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .a11y-grid-secondary {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 400px) {
  .a11y-grid {
    grid-template-columns: repeat(1, 1fr);
  }

  .a11y-grid-secondary {
    grid-template-columns: 1fr !important;
  }
}

/* Screen reader only */
.sr-only,
.a11y-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

#a11y-widget-trigger:focus-visible,
.a11y-close:focus-visible,
.a11y-reset:focus-visible,
.brutalist-button:focus-visible,
.a11y-profile-card:focus-visible,
.a11y-profiles-header:focus-visible,
.a11y-lang-btn:focus-visible,
.a11y-lang-header:focus-visible {
  outline: 3px solid #0f766e !important;
  outline-offset: 3px !important;
}

@media (forced-colors: active) {
  #a11y-widget-trigger,
  #a11y-widget-panel,
  .brutalist-button,
  .a11y-profile-card,
  .a11y-lang-btn,
  .a11y-reset,
  .a11y-close {
    forced-color-adjust: auto;
    border: 1px solid ButtonText;
  }

  .a11y-card-check {
    forced-color-adjust: auto;
  }
}

/* === PROFILES SECTION STYLES (GRID DESIGN) === */
.a11y-profiles-wrapper {
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.a11y-profiles-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 600;
  color: var(--a11y-text-main);
  margin-bottom: 12px;
  padding: 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: start;
  font-family: var(--a11y-font-family) !important;
}

.a11y-profiles-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 5px 5px 10px 5px;
}

/* Brutalist Profile Card & Lang Button Styles (Mimic .brutalist-button) */
.a11y-profile-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px; /* Inner gap */
  padding: 10px 12px;
  border: 2px solid transparent;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--a11y-text-main);
  text-align: center;
  position: relative;
  width: 100%;
  flex-direction: row; /* Profiles horizontal again */
  justify-content: flex-start;
  text-align: left;
}

.a11y-lang-btn {
  justify-content: flex-start; /* Lang buttons left aligned */
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px; /* Inner gap */
  padding: 4px 16px;
  border: 2px solid transparent;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--a11y-text-main);
  text-align: center;
  position: relative;
  width: 100%;
}

/* Hover State */
.a11y-profile-card:hover,
.a11y-lang-btn:hover {
  border-color: var(--a11y-primary);
}

/* Active State */
.a11y-profile-card.active,
.a11y-lang-btn.active {
  border: 2px solid var(--a11y-primary);
  box-shadow: 0 0 0 5px rgb(from var(--a11y-primary) r g b / 0.25);
  background-color: rgba(255, 255, 255, 0.8);
}

/* Inner icon styles */
.a11y-profile-icon,
.a11y-lang-flag {
  color: var(--a11y-text-muted);
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.a11y-lang-flag {
  margin-bottom: 0; /* Lang flag is side-by-side */
  font-size: 32px; /* Bigger flags */
}

.a11y-profile-icon svg {
  width: 28px;
  height: 28px;
  fill: currentColor;
}

.a11y-profile-card.active .a11y-profile-icon {
  color: var(--a11y-primary);
  transform: scale(1.1);
}

/* Inner Text Styles (Mimic .button-text) */
.a11y-profile-title,
.a11y-lang-name {
  line-height: 1.2;
}

.a11y-profile-title span,
.a11y-lang-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--a11y-text-main);
}

.a11y-profile-card.active .a11y-profile-title span,
.a11y-lang-btn.active .a11y-lang-name {
  color: var(--a11y-primary);
}

/* Check Icon for Profiles (Mimic .a11y-card-check) */
.a11y-profile-check {
  display: none; /* User didn't ask for check, just style mathcing. But brutalist has check. */
}

/* Language Selector Module Structure */
.a11y-lang-selector-root {
  border-bottom: 2px solid rgba(0, 0, 0, 0.1); /* Subtle divider */
  margin-bottom: 12px;
}

.a11y-lang-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 12px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  font-size: 16px;
  color: var(--a11y-text-main);
}

.a11y-disclosure-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.a11y-lang-toggle-icon {
  transform: rotate(-90deg);
  transition: transform 0.3s ease-out;
}

.a11y-lang-search {
  padding: 10px 0;
  position: sticky;
  top: 0;
  z-index: 10;
  background: transparent; /* backdrop filter from panel */
}

.a11y-lang-search input {
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  font-family: inherit;
  font-weight: 500;
  outline: 2px solid transparent;
  outline-offset: 2px;
  background: rgba(255, 255, 255, 0.5);
}

.a11y-lang-search input:focus {
  border-color: var(--a11y-primary);
  background: #fff;
  outline-color: var(--a11y-primary);
}

.a11y-lang-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 5px 5px 10px 5px;
  max-height: 300px; /* Taller */
  overflow-y: auto;
}

/* Scrollbar for grid */
.a11y-lang-grid::-webkit-scrollbar {
  width: 6px;
}
.a11y-lang-grid::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

/* Responsive adjustment for profiles */
@media (max-width: 480px) {
  .a11y-lang-grid {
    grid-template-columns: 1fr;
  }
}

#a11y-widget-panel[hidden] {
  display: none !important;
}

@media (prefers-reduced-motion: reduce) {
  #a11y-widget-trigger,
  #a11y-widget-trigger::before,
  #a11y-widget-trigger svg,
  #a11y-widget-panel,
  #a11y-widget-panel *,
  .brutalist-button,
  .feature-icon,
  .a11y-profile-card,
  .a11y-lang-btn {
    animation: none !important;
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
`
    shadowRoot.appendChild(style)

    const container = document.createElement('div')
    container.innerHTML = `<div>
      <button
        type="button"
        id="a11y-widget-trigger"
        aria-label="Open accessibility preferences"
        aria-expanded="false"
        aria-controls="a11y-widget-panel"
        title="Erişilebilirlik Menüsü"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2M21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"
          />
        </svg>
      </button>
      <div
        id="a11y-widget-panel"
        role="dialog"
        aria-labelledby="a11y-title"
        aria-describedby="a11y-scope-note"
        aria-modal="true"
        hidden
        inert
      >
        <div class="a11y-header">
          <div>
            <h2 id="a11y-title">ERİŞİLEBİLİRLİK ARAÇLARI</h2>
            <small id="a11y-scope-note" data-i18n="wcagFocus"
              >WCAG 2.2-focused</small
            >
          </div>
          <button type="button" class="a11y-close" aria-label="Kapat">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              />
            </svg>
          </button>
        </div>

        <div class="a11y-content">
          <div id="a11y-profiles-root">
          </div>

          <div class="a11y-category">
            <div class="a11y-grid" id="a11y-widget-panel-buttons">
            </div>
          </div>

          <div class="a11y-category">
            <div class="a11y-grid a11y-grid-secondary">
            </div>
          </div>
        </div>

        <div class="a11y-footer">
          <button type="button" class="a11y-reset" id="a11y-reset-btn">
            AYARLARI SIFIRLA
          </button>
          <p
            id="a11y-widget-status"
            class="a11y-visually-hidden"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          ></p>
        </div>
      </div>
    </div>`
    while (container.firstChild) {
      shadowRoot.appendChild(container.firstChild)
    }

    /**
 * Çoklu Dil Desteği Modülü
 */

const TRANSLATIONS = {
  tr: {
    langName: 'Türkçe',
    flag: '🇹🇷',
    widgetTitle: 'ERİŞİLEBİLİRLİK ARAÇLARI',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'AYARLARI SIFIRLA',
    close: 'Kapat',
    profilesHeader: 'Erişilebilirlik Profilleri',
    'seizure-safe': 'Azaltılmış Hareket',
    'vision-impaired': 'Büyük Yazı ve Kontrast',
    'adhd-friendly': 'Odak Desteği',
    'cognitive-disability': 'Okuma Odağı',
    'motor-impaired': 'Bedensel Engel',
    dyslexia: 'Okunabilir Tipografi',
    'color-blind': 'Renk Ayrımı',
    fontSizeBtn: 'Yazı Boyutu',
    highContrastBtn: 'Kontrast',
    contrastDark: 'Koyu Kontrast',
    contrastLight: 'Açık Kontrast',
    contrastWarm: 'Sıcak Kontrast',
    contrastBlue: 'Mavi Kontrast',
    monochromeBtn: 'Monokrom',
    readingGuideBtn: 'Okuma Rehberi',
    pauseAnimationsBtn: 'Animasyonları Durdur',
    hideImagesBtn: 'Resimleri Gizle',
    highlightLinksBtn: 'Linkleri Vurgula',
    highlightTitlesBtn: 'Başlıkları Vurgula',
    cursorBtn: 'Büyük İmleç',
    lineHeightBtn: 'Satır Yüksekliği',
    letterSpacingBtn: 'Harf Aralığı',
    textAlignBtn: 'Metin Hizala',
    fontFamilyBtn: 'Yazı Tipi',
    tooltipBtn: 'İpuçları',
    skipLink: 'İçeriğe Atla',
    skipNavigationBtn: 'İçeriğe Atla',
    focusIndicatorBtn: 'Odak Göstergesi',
    largeCursorBtn: 'Büyük İmleç',
    languageHeader: 'Dil Seçimi',
    searchLanguage: 'Dil Ara...',
    previous: 'Önceki',
    next: 'Sonraki',
    loading: 'Yükleniyor...',
    videoNotFound: 'Video bulunamadı',
    audioDescriptionBtn: 'Sesli Oku',
    replay: 'Tekrarla',
    play: 'Oynat',
    pause: 'Duraklat',
  },
  en: {
    langName: 'English',
    flag: '🇬🇧',
    widgetTitle: 'ACCESSIBILITY TOOLS',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'RESET SETTINGS',
    close: 'Close',
    profilesHeader: 'Accessibility Profiles',
    'seizure-safe': 'Reduced Motion',
    'vision-impaired': 'Large Text & Contrast',
    'adhd-friendly': 'Focus Support',
    'cognitive-disability': 'Reading Focus',
    'motor-impaired': 'Motor Impaired',
    dyslexia: 'Readable Typography',
    'color-blind': 'Color Differentiation',
    fontSizeBtn: 'Text Size',
    highContrastBtn: 'Contrast',
    contrastDark: 'Dark Contrast',
    contrastLight: 'Light Contrast',
    contrastWarm: 'Warm Contrast',
    contrastBlue: 'Blue Contrast',
    monochromeBtn: 'Monochrome',
    readingGuideBtn: 'Reading Guide',
    pauseAnimationsBtn: 'Pause Animations',
    hideImagesBtn: 'Hide Images',
    highlightLinksBtn: 'Highlight Links',
    highlightTitlesBtn: 'Highlight Titles',
    cursorBtn: 'Big Cursor',
    lineHeightBtn: 'Line Height',
    letterSpacingBtn: 'Letter Spacing',
    textAlignBtn: 'Text Align',
    fontFamilyBtn: 'Font Family',
    tooltipBtn: 'Tooltips',
    skipLink: 'Skip to Content',
    skipNavigationBtn: 'Skip to Content',
    focusIndicatorBtn: 'Focus Indicator',
    largeCursorBtn: 'Big Cursor',
    languageHeader: 'Select Language',
    searchLanguage: 'Search Language...',
    previous: 'Previous',
    next: 'Next',
    loading: 'Loading...',
    videoNotFound: 'Video not found',
    audioDescriptionBtn: 'Read Aloud',
    replay: 'Replay',
    play: 'Play',
    pause: 'Pause',
  },
  de: {
    langName: 'Deutsch',
    flag: '🇩🇪',
    widgetTitle: 'BARRIEREFREIHEITS-TOOLS',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'EINSTELLUNGEN ZURÜCKSETZEN',
    close: 'Schließen',
    profilesHeader: 'Barrierefreiheits-Profile',
    'seizure-safe': 'Reduzierte Bewegung',
    'vision-impaired': 'Großer Text & Kontrast',
    'adhd-friendly': 'Fokusunterstützung',
    'cognitive-disability': 'Lesefokus',
    'motor-impaired': 'Motorisch Eingeschränkt',
    dyslexia: 'Lesbare Typografie',
    'color-blind': 'Farbunterscheidung',
    fontSizeBtn: 'Schriftgröße',
    highContrastBtn: 'Kontrast',
    contrastDark: 'Dunkler Kontrast',
    contrastLight: 'Heller Kontrast',
    contrastWarm: 'Warmer Kontrast',
    contrastBlue: 'Blauer Kontrast',
    monochromeBtn: 'Monochrom',
    readingGuideBtn: 'Lesehilfe',
    pauseAnimationsBtn: 'Animationen Anhalten',
    hideImagesBtn: 'Bilder Ausblenden',
    highlightLinksBtn: 'Links Hervorheben',
    highlightTitlesBtn: 'Titel Hervorheben',
    cursorBtn: 'Großer Cursor',
    lineHeightBtn: 'Zeilenhöhe',
    letterSpacingBtn: 'Buchstabenabstand',
    textAlignBtn: 'Textausrichtung',
    fontFamilyBtn: 'Schriftart',
    tooltipBtn: 'Tooltips',
    skipLink: 'Zum Inhalt Springen',
    skipNavigationBtn: 'Zum Inhalt Springen',
    focusIndicatorBtn: 'Fokus-Indikator',
    largeCursorBtn: 'Großer Cursor',
    languageHeader: 'Sprache',
    searchLanguage: 'Suchen...',
    previous: 'Zurück',
    next: 'Weiter',
    loading: 'Laden...',
    videoNotFound: 'Video nicht gefunden',
    audioDescriptionBtn: 'Vorlesen',
    replay: 'Wiederholen',
    play: 'Abspielen',
    pause: 'Pause',
  },
  fr: {
    langName: 'Français',
    flag: '🇫🇷',
    widgetTitle: "OUTILS D'ACCESSIBILITÉ",
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'RÉINITIALISER',
    close: 'Fermer',
    profilesHeader: "Profils d'Accessibilité",
    'seizure-safe': 'Mouvement réduit',
    'vision-impaired': 'Grand texte et contraste',
    'adhd-friendly': 'Aide à la concentration',
    'cognitive-disability': 'Focus de lecture',
    'motor-impaired': 'Handicap Moteur',
    dyslexia: 'Typographie lisible',
    'color-blind': 'Distinction des couleurs',
    fontSizeBtn: 'Taille du Texte',
    highContrastBtn: 'Contraste',
    contrastDark: 'Contraste Sombre',
    contrastLight: 'Contraste Clair',
    contrastWarm: 'Contraste Chaud',
    contrastBlue: 'Contraste Bleu',
    monochromeBtn: 'Monochrome',
    readingGuideBtn: 'Guide de Lecture',
    pauseAnimationsBtn: 'Pause Animations',
    hideImagesBtn: 'Masquer Images',
    highlightLinksBtn: 'Surligner Liens',
    highlightTitlesBtn: 'Surligner Titres',
    cursorBtn: 'Grand Curseur',
    lineHeightBtn: 'Hauteur de Ligne',
    letterSpacingBtn: 'Espacement Lettres',
    textAlignBtn: 'Alignement Texte',
    fontFamilyBtn: 'Police',
    tooltipBtn: 'Infobulles',
    skipLink: 'Aller au Contenu',
    skipNavigationBtn: 'Aller au Contenu',
    focusIndicatorBtn: 'Indicateur de Focus',
    largeCursorBtn: 'Grand Curseur',
    languageHeader: 'Langue',
    searchLanguage: 'Rechercher...',
    previous: 'Précédent',
    next: 'Suivant',
    loading: 'Chargement...',
    videoNotFound: 'Vidéo non trouvée',
    audioDescriptionBtn: 'Lire à voix haute',
    replay: 'Rejouer',
    play: 'Lire',
    pause: 'Pause',
  },
  es: {
    langName: 'Español',
    flag: '🇪🇸',
    widgetTitle: 'HERRAMIENTAS DE ACCESIBILIDAD',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'RESTABLECER',
    close: 'Cerrar',
    profilesHeader: 'Perfiles de Accesibilidad',
    'seizure-safe': 'Movimiento reducido',
    'vision-impaired': 'Texto grande y contraste',
    'adhd-friendly': 'Apoyo al enfoque',
    'cognitive-disability': 'Enfoque de lectura',
    'motor-impaired': 'Discapacidad Motora',
    dyslexia: 'Tipografía legible',
    'color-blind': 'Distinción de colores',
    fontSizeBtn: 'Tamaño de Texto',
    highContrastBtn: 'Contraste',
    contrastDark: 'Contraste Oscuro',
    contrastLight: 'Contraste Claro',
    contrastWarm: 'Contraste Cálido',
    contrastBlue: 'Contraste Azul',
    monochromeBtn: 'Monocromo',
    readingGuideBtn: 'Guía de Lectura',
    pauseAnimationsBtn: 'Pausar Animaciones',
    hideImagesBtn: 'Ocultar Imágenes',
    highlightLinksBtn: 'Resaltar Enlaces',
    highlightTitlesBtn: 'Resaltar Títulos',
    cursorBtn: 'Cursor Grande',
    lineHeightBtn: 'Altura de Línea',
    letterSpacingBtn: 'Espaciado',
    textAlignBtn: 'Alineación',
    fontFamilyBtn: 'Fuente',
    tooltipBtn: 'Tooltips',
    skipLink: 'Saltar al Contenido',
    skipNavigationBtn: 'Saltar al Contenido',
    focusIndicatorBtn: 'Indicador de Foco',
    largeCursorBtn: 'Cursor Grande',
    languageHeader: 'Idioma',
    searchLanguage: 'Buscar...',
    previous: 'Anterior',
    next: 'Siguiente',
    loading: 'Cargando...',
    videoNotFound: 'Video no encontrado',
    audioDescriptionBtn: 'Leer en voz alta',
    replay: 'Repetir',
    play: 'Reproducir',
    pause: 'Pausa',
  },
  it: {
    langName: 'Italiano',
    flag: '🇮🇹',
    widgetTitle: 'STRUMENTI DI ACCESSIBILITÀ',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'RIPRISTINA',
    close: 'Chiudi',
    profilesHeader: 'Profili di Accessibilità',
    'seizure-safe': 'Movimento ridotto',
    'vision-impaired': 'Testo grande e contrasto',
    'adhd-friendly': 'Supporto alla concentrazione',
    'cognitive-disability': 'Focus di lettura',
    'motor-impaired': 'Disabilità Motoria',
    dyslexia: 'Tipografia leggibile',
    'color-blind': 'Distinzione dei colori',
    fontSizeBtn: 'Dimensione Testo',
    highContrastBtn: 'Contrasto',
    contrastDark: 'Contrasto Scuro',
    contrastLight: 'Contrasto Chiaro',
    contrastWarm: 'Contrasto Caldo',
    contrastBlue: 'Contrasto Blu',
    monochromeBtn: 'Monocromatico',
    readingGuideBtn: 'Guida alla Lettura',
    pauseAnimationsBtn: 'Pausa Animazioni',
    hideImagesBtn: 'Nascondi Immagini',
    highlightLinksBtn: 'Evidenzia Link',
    highlightTitlesBtn: 'Evidenzia Titoli',
    cursorBtn: 'Cursore Grande',
    lineHeightBtn: 'Altezza Riga',
    letterSpacingBtn: 'Spaziatura Lettere',
    textAlignBtn: 'Allineamento',
    fontFamilyBtn: 'Carattere',
    tooltipBtn: 'Suggerimenti',
    skipLink: 'Vai al Contenuto',
    skipNavigationBtn: 'Vai al Contenuto',
    focusIndicatorBtn: 'Indicatore Focus',
    largeCursorBtn: 'Cursore Grande',
    languageHeader: 'Lingua',
    searchLanguage: 'Cerca...',
    previous: 'Precedente',
    next: 'Successivo',
    loading: 'Caricamento...',
    videoNotFound: 'Video non trovato',
    audioDescriptionBtn: 'Leggi ad alta voce',
    replay: 'Ripeti',
    play: 'Riproduci',
    pause: 'Pausa',
  },
  pt: {
    langName: 'Português',
    flag: '🇵🇹',
    widgetTitle: 'FERRAMENTAS DE ACESSIBILIDADE',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'REDEFINIR',
    close: 'Fechar',
    profilesHeader: 'Perfis de Acessibilidade',
    'seizure-safe': 'Movimento reduzido',
    'vision-impaired': 'Texto grande e contraste',
    'adhd-friendly': 'Suporte ao foco',
    'cognitive-disability': 'Foco de leitura',
    'motor-impaired': 'Deficiência Motora',
    dyslexia: 'Tipografia legível',
    'color-blind': 'Distinção de cores',
    fontSizeBtn: 'Tamanho do Texto',
    highContrastBtn: 'Contraste',
    contrastDark: 'Contraste Escuro',
    contrastLight: 'Contraste Claro',
    contrastWarm: 'Contraste Quente',
    contrastBlue: 'Contraste Azul',
    monochromeBtn: 'Monocromático',
    readingGuideBtn: 'Guia de Leitura',
    pauseAnimationsBtn: 'Pausar Animações',
    hideImagesBtn: 'Ocultar Imagens',
    highlightLinksBtn: 'Destacar Links',
    highlightTitlesBtn: 'Destacar Títulos',
    cursorBtn: 'Cursor Grande',
    lineHeightBtn: 'Altura da Linha',
    letterSpacingBtn: 'Espaçamento',
    textAlignBtn: 'Alinhamento',
    fontFamilyBtn: 'Fonte',
    tooltipBtn: 'Dicas',
    skipLink: 'Pular para Conteúdo',
    skipNavigationBtn: 'Pular para Conteúdo',
    focusIndicatorBtn: 'Indicador de Foco',
    largeCursorBtn: 'Cursor Grande',
    languageHeader: 'Idioma',
    searchLanguage: 'Pesquisar...',
    previous: 'Anterior',
    next: 'Próxima',
    loading: 'Carregando...',
    videoNotFound: 'Vídeo não encontrado',
    audioDescriptionBtn: 'Ler em voz alta',
    replay: 'Repetir',
    play: 'Reproduzir',
    pause: 'Pausa',
  },
  ru: {
    langName: 'Русский',
    flag: '🇷🇺',
    widgetTitle: 'ИНСТРУМЕНТЫ ДОСТУПНОСТИ',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'СБРОСИТЬ',
    close: 'Закрыть',
    profilesHeader: 'Профили Доступности',
    'seizure-safe': 'Снижение движения',
    'vision-impaired': 'Крупный текст и контраст',
    'adhd-friendly': 'Поддержка концентрации',
    'cognitive-disability': 'Фокус чтения',
    'motor-impaired': 'Моторные Нарушения',
    dyslexia: 'Читаемая типографика',
    'color-blind': 'Различение цветов',
    fontSizeBtn: 'Размер Текста',
    highContrastBtn: 'Контраст',
    contrastDark: 'Тёмный Контраст',
    contrastLight: 'Светлый Контраст',
    contrastWarm: 'Тёплый Контраст',
    contrastBlue: 'Синий Контраст',
    monochromeBtn: 'Монохром',
    readingGuideBtn: 'Гид по Чтению',
    pauseAnimationsBtn: 'Пауза Анимации',
    hideImagesBtn: 'Скрыть Изображения',
    highlightLinksBtn: 'Выделить Ссылки',
    highlightTitlesBtn: 'Выделить Заголовки',
    cursorBtn: 'Большой Курсор',
    lineHeightBtn: 'Высота Строки',
    letterSpacingBtn: 'Интервал',
    textAlignBtn: 'Выравнивание',
    fontFamilyBtn: 'Шрифт',
    tooltipBtn: 'Подсказки',
    skipLink: 'К Содержимому',
    skipNavigationBtn: 'К Содержимому',
    focusIndicatorBtn: 'Индикатор Фокуса',
    largeCursorBtn: 'Большой Курсор',
    languageHeader: 'Язык',
    searchLanguage: 'Поиск...',
    previous: 'Назад',
    next: 'Вперед',
    loading: 'Загрузка...',
    videoNotFound: 'Видео не найдено',
    audioDescriptionBtn: 'Читать вслух',
    replay: 'Повторить',
    play: 'Воспроизвести',
    pause: 'Пауза',
  },
  ja: {
    langName: '日本語',
    flag: '🇯🇵',
    widgetTitle: 'アクセシビリティツール',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: '設定をリセット',
    close: '閉じる',
    profilesHeader: 'アクセシビリティプロファイル',
    'seizure-safe': '動きを抑える',
    'vision-impaired': '大きな文字と高コントラスト',
    'adhd-friendly': '集中サポート',
    'cognitive-disability': '読書フォーカス',
    'motor-impaired': '運動障害',
    dyslexia: '読みやすい書体',
    'color-blind': '色の区別',
    fontSizeBtn: '文字サイズ',
    highContrastBtn: 'コントラスト',
    contrastDark: 'ダーク',
    contrastLight: 'ライト',
    contrastWarm: 'ウォーム',
    contrastBlue: 'ブルー',
    monochromeBtn: 'モノクロ',
    readingGuideBtn: '読書ガイド',
    pauseAnimationsBtn: 'アニメーション停止',
    hideImagesBtn: '画像を隠す',
    highlightLinksBtn: 'リンクを強調',
    highlightTitlesBtn: 'タイトルを強調',
    cursorBtn: '大きなカーソル',
    lineHeightBtn: '行の高さ',
    letterSpacingBtn: '文字間隔',
    textAlignBtn: 'テキスト配置',
    fontFamilyBtn: 'フォント',
    tooltipBtn: 'ツールチップ',
    skipLink: 'コンテンツへ移動',
    skipNavigationBtn: 'コンテンツへ移動',
    focusIndicatorBtn: 'フォーカス表示',
    largeCursorBtn: '大きなカーソル',
    languageHeader: '言語',
    searchLanguage: '検索...',
    previous: '前へ',
    next: '次へ',
    loading: '読み込み中...',
    videoNotFound: 'ビデオが見つかりません',
    audioDescriptionBtn: '読み上げ',
    replay: 'リプレイ',
    play: '再生',
    pause: '一時停止',
  },
  zh: {
    langName: '简体中文',
    flag: '🇨🇳',
    widgetTitle: '无障碍工具',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: '重置设置',
    close: '关闭',
    profilesHeader: '无障碍配置文件',
    'seizure-safe': '减少动态效果',
    'vision-impaired': '大字体和高对比度',
    'adhd-friendly': '专注支持',
    'cognitive-disability': '阅读聚焦',
    'motor-impaired': '运动障碍',
    dyslexia: '易读字体',
    'color-blind': '颜色区分',
    fontSizeBtn: '字体大小',
    highContrastBtn: '高对比度',
    contrastDark: '暗色对比',
    contrastLight: '亮色对比',
    contrastWarm: '暖色对比',
    contrastBlue: '蓝色对比',
    monochromeBtn: '单色',
    readingGuideBtn: '阅读指南',
    pauseAnimationsBtn: '暂停动画',
    hideImagesBtn: '隐藏图片',
    highlightLinksBtn: '高亮链接',
    highlightTitlesBtn: '高亮标题',
    cursorBtn: '大光标',
    lineHeightBtn: '行高',
    letterSpacingBtn: '字间距',
    textAlignBtn: '文本对齐',
    fontFamilyBtn: '字体',
    tooltipBtn: '工具提示',
    skipLink: '跳转到内容',
    skipNavigationBtn: '跳转到内容',
    focusIndicatorBtn: '焦点指示',
    largeCursorBtn: '大光标',
    languageHeader: '语言',
    searchLanguage: '搜索...',
    previous: '上一个',
    next: '下一个',
    loading: '加载中...',
    videoNotFound: '未找到视频',
    audioDescriptionBtn: '朗读',
    replay: '重播',
    play: '播放',
    pause: '暂停',
  },
  ar: {
    langName: 'العربية',
    flag: '🇸🇦',
    widgetTitle: 'أدوات الوصول',
    wcagFocus: 'WCAG 2.2-focused',
    resetSettings: 'إعادة تعيين الإعدادات',
    close: 'إغلاق',
    profilesHeader: 'ملفات تعريف الوصول',
    'seizure-safe': 'حركة مخفّضة',
    'vision-impaired': 'نص كبير وتباين عالٍ',
    'adhd-friendly': 'دعم التركيز',
    'cognitive-disability': 'تركيز القراءة',
    'motor-impaired': 'إعاقة حركية',
    dyslexia: 'طباعة سهلة القراءة',
    'color-blind': 'تمييز الألوان',
    fontSizeBtn: 'حجم النص',
    highContrastBtn: 'التباين',
    contrastDark: 'تباين داكن',
    contrastLight: 'تباين فاتح',
    contrastWarm: 'تباين دافئ',
    contrastBlue: 'تباين أزرق',
    monochromeBtn: 'أحادية اللون',
    readingGuideBtn: 'دليل القراءة',
    pauseAnimationsBtn: 'إيقاف الرسوم المتحركة',
    hideImagesBtn: 'إخفاء الصور',
    highlightLinksBtn: 'تمييز الروابط',
    highlightTitlesBtn: 'تمييز العناوين',
    cursorBtn: 'مؤشر كبير',
    lineHeightBtn: 'ارتفاع الخط',
    letterSpacingBtn: 'تباعد الأحرف',
    textAlignBtn: 'محاذاة النص',
    fontFamilyBtn: 'نوع الخط',
    tooltipBtn: 'تلميحات',
    skipLink: 'تخطي إلى المحتوى',
    skipNavigationBtn: 'تخطي إلى المحتوى',
    focusIndicatorBtn: 'مؤشر التركيز',
    largeCursorBtn: 'مؤشر كبير',
    languageHeader: 'لغة',
    searchLanguage: 'بحث...',
    previous: 'سابق',
    next: 'التالي',
    loading: 'جار التحميل...',
    videoNotFound: 'الفيديو غير موجود',
    audioDescriptionBtn: 'قراءة بصوت عالٍ',
    replay: 'إعادة',
    play: 'تشغيل',
    pause: 'إيقاف مؤقت',
  },
}

// Varsayılan Dil
let CURRENT_LANG = 'tr'

/**
 * Çeviri fonksiyonu
 * @param {string} key - Çeviri anahtarı
 * @returns {string} Çevrilmiş metin veya key
 */
function t(key) {
  return TRANSLATIONS[CURRENT_LANG][key] || key
}

/**
 * Dili değiştirir
 */
function setLanguage(lang, root) {
  if (!TRANSLATIONS[lang]) return
  CURRENT_LANG = lang
  localStorage.setItem('a11y-lang', lang)

  // RTL Support
  const host = root.host ? root.host.shadowRoot : root
  const panel = host.getElementById('a11y-widget-panel')
  if (panel) {
    if (lang === 'ar') {
      panel.setAttribute('dir', 'rtl')
      panel.classList.add('a11y-rtl')
    } else {
      panel.setAttribute('dir', 'ltr')
      panel.classList.remove('a11y-rtl')
    }
  }

  updateAllTexts(root)
}

/**
 * UI'daki tüm metinleri günceller
 */
function updateAllTexts(root) {
  const host = root.host ? root.host.shadowRoot : root

  // 1. Statik metinler (data-i18n attribute'u olanlar)
  host.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n')
    el.innerText = t(key)
  })

  // 2. Statik ID'li elemanlar (widget.html içindekiler)
  const idMap = {
    'a11y-title': 'widgetTitle',
    'a11y-reset-btn': 'resetSettings',
  }

  Object.keys(idMap).forEach(id => {
    const el = host.getElementById(id)
    if (el) el.innerText = t(idMap[id])
  })

  // 3. Buton metinleri
  host.querySelectorAll('.brutalist-button').forEach(btn => {
    const textSpan = btn.querySelector('.button-text span')
    if (textSpan) {
      const translation = t(btn.id)
      if (translation !== btn.id) {
        textSpan.innerText = translation
      }
    }
  })

  // 4. Profil metinleri
  if (typeof updateProfileTexts === 'function') {
    updateProfileTexts(host)
  }

  // 5. Dil seçiciyi güncelle (Varsa)
  if (typeof window.updateLanguageSelectorUI === 'function') {
    window.updateLanguageSelectorUI()
  }

  // 6. Skip Link metnini güncelle (Varsa)
  if (typeof window.updateSkipLinkText === 'function') {
    window.updateSkipLinkText()
  }

  // 7. Read Aloud Player Update
  if (typeof window.updateAudioDescriptionTexts === 'function') {
    window.updateAudioDescriptionTexts()
  }
}

/**
 * Başlangıç dilini ayarlar
 */
function initLanguage(root) {
  const saved = localStorage.getItem('a11y-lang')
  if (saved && TRANSLATIONS[saved]) {
    CURRENT_LANG = saved
  } else {
    // Auto-detect from browser
    const browserLang = navigator.language.split('-')[0] // 'en-US' -> 'en'
    if (TRANSLATIONS[browserLang]) {
      CURRENT_LANG = browserLang
    } else {
      CURRENT_LANG = 'tr' // Default fallback
    }
  }
}

// Global'e aç
window.a11yI18n = {
  t,
  setLanguage,
  initLanguage,
  getCurrentLang: () => CURRENT_LANG,
  getLanguages: () =>
    Object.keys(TRANSLATIONS).map(key => ({
      code: key,
      name: TRANSLATIONS[key].langName || key.toUpperCase(),
      flag: TRANSLATIONS[key].flag || '🌐',
    })),
}

/**
 * Read Aloud Feature (Web Speech API)
 * Reads text content aloud using SpeechSynthesis with a visual player UI.
 * Users hover/click to select elements, and can navigate through them using the player.
 * @param {ShadowRoot} root - The shadow root of the widget
 * @param {Function} initButton - Function to initialize the button
 * @param {Function} toggleFeature - Function to toggle the feature state
 * @param {Function} registerReset - Function to register reset handler
 */

let CURRENT_AUDIO_DESC_STATE = false

function audioDescription(
  root,
  initButton,
  toggleFeature,
  registerReset,
  runtime = {},
) {
  const button = {
    id: 'audioDescriptionBtn',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>`,
    name: 'Sesli Oku',
    type: 'toggle',
  }

  // Helper to translate safely
  function getTranslation(key, fallback) {
    if (window.a11yI18n && typeof window.a11yI18n.t === 'function') {
      return window.a11yI18n.t(key)
    }
    return fallback
  }

  // --- Styles ---
  function injectStyles() {
    if (!document.getElementById('a11y-audio-desc-style')) {
      const style = prepareWidgetStyle(document.createElement('style'))
      style.id = 'a11y-audio-desc-style'
      style.textContent = `
        /* Highlight Style */
        .a11y-ad-highlight {
            outline: 3px solid #0d9488 !important;
            outline-offset: 2px;
            background-color: rgba(13, 148, 136, 0.1) !important;
            cursor: pointer !important;
            transition: all 0.2s ease;
        }

        /* Hover Effect (when feature is on) */
        body.a11y-ad-enabled *:hover {
             cursor: help;
        }

        body.a11y-ad-enabled .a11y-ad-hover:not(.a11y-ad-highlight) {
            outline: 2px dashed #0d9488 !important;
            outline-offset: 2px;
            background-color: rgba(13, 148, 136, 0.05) !important;
        }

        /* Player Container with Glassmorphism */
        .a11y-ad-player {
            position: fixed;
            bottom: 90px;
            right: 25px;
            width: 380px;

            /* Glassmorphism Base */
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(12px) saturate(180%);
            -webkit-backdrop-filter: blur(12px) saturate(180%);
            border: 1px solid rgba(209, 213, 219, 0.5); /* Gray-300 */
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);

            border-radius: 16px;
            z-index: 10000;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 0.3s, transform 0.3s;
            pointer-events: none;
        }
        .a11y-ad-player.visible {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
        }

        /* Header / Visualizer Area */
        .a11y-ad-header {
            padding: 16px;
            background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            position: relative;
        }

        .a11y-ad-icon-animation {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: rgba(255,255,255,0.2);
            animation: pulse-ring 2s infinite;
        }

        @keyframes pulse-ring {
            0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
            70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
        }

        /* Content Area */
        .a11y-ad-content {
            padding: 16px;
        }
        .a11y-ad-text-display {
            font-size: 15px;
            line-height: 1.6;
            color: #1f2937;
            margin-bottom: 20px;
            max-height: 120px;
            overflow-y: auto;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 12px;
            font-weight: 500;
        }

        /* Controls */
        .a11y-ad-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
        }

        .a11y-ad-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: 50%; /* Circle shape for all */
            background: transparent;
            border: 1px solid transparent;
            color: #4b5563;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        .a11y-ad-btn:hover:not(:disabled) {
            background: rgba(0, 0, 0, 0.05);
            color: #111827;
            transform: scale(1.05); /* Gentle scale */
        }
        .a11y-ad-btn:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }

        .a11y-ad-btn-lg {
            width: 48px;
            height: 48px;
            background: #0d9488; /* Keep primary color for play */
            color: white;
            box-shadow: 0 4px 10px rgba(13, 148, 136, 0.2);
        }
        .a11y-ad-btn-lg:hover:not(:disabled) {
            background: #0f766e;
            transform: scale(1.05);
            box-shadow: 0 6px 12px rgba(13, 148, 136, 0.3);
        }

        /* Close Button - Red X Style */
        .a11y-ad-close {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 32px;
            height: 32px;
            background: #ef4444; /* Red-500 */
            color: white;
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer !important;
            z-index: 20;
            transition: all 0.2s;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            padding: 0;
        }
        .a11y-ad-close:hover {
            background: #dc2626; /* Red-600 */
            transform: scale(1.1) rotate(90deg);
            color: white;
        }
        .a11y-ad-close svg {
            width: 18px;
            height: 18px;
            stroke-width: 2.5;
        }

        /* Ensure player elements don't inherit the help cursor */
        .a11y-ad-player, .a11y-ad-player * {
             cursor: default !important;
        }

        .a11y-ad-btn {
             cursor: pointer !important;
        }

        /* Mobile Responsive */
        @media (max-width: 500px) {
            .a11y-ad-player {
                width: 94%;
                right: 3%;
                left: 3%;
                bottom: 100px;
            }
        }
      `
      document.head.appendChild(style)
    }
  }

  function removeStyles() {
    const style = document.getElementById('a11y-audio-desc-style')
    if (style) style.remove()
  }

  // --- Dynamic Text Update ---
  window.updateAudioDescriptionTexts = function () {
    if (!playerEl) return

    const tTitle = getTranslation('audioDescriptionBtn', 'Sesli Oku')
    const tClose = getTranslation('close', 'Kapat')
    const tPrev = getTranslation('previous', 'Önceki')
    const tNext = getTranslation('next', 'Sonraki')
    const tPlay = getTranslation('play', 'Oynat')
    const tPause = getTranslation('pause', 'Duraklat')
    const tReplay = getTranslation('replay', 'Tekrarla')

    // Update Title
    const titleEl = playerEl.querySelector('#a11y-ad-title')
    if (titleEl) titleEl.innerText = tTitle

    // Update Close Button
    const closeBtn = playerEl.querySelector('.a11y-ad-close')
    if (closeBtn) closeBtn.setAttribute('title', tClose)

    // Update Controls
    const prevBtn = playerEl.querySelector('#a11y-ad-prev')
    if (prevBtn) prevBtn.setAttribute('title', tPrev)

    const nextBtn = playerEl.querySelector('#a11y-ad-next')
    if (nextBtn) nextBtn.setAttribute('title', tNext)

    const replayBtn = playerEl.querySelector('#a11y-ad-replay')
    if (replayBtn) replayBtn.setAttribute('title', tReplay)

    // Update Play/Pause title based on current state (tricky, but we can set base title)
    // Since play/pause toggles, we might just leave the title as main action or update dynamically in toggle
    const playPauseBtn = playerEl.querySelector('#a11y-ad-play-pause')
    if (playPauseBtn) {
      // Just update the generic title or current state if possible
      // For now let's set it to Play as safe default or check icon visibility
      const isPlaying =
        playPauseBtn.querySelector('#a11y-ad-icon-pause').style.display !==
        'none'
      playPauseBtn.setAttribute('title', isPlaying ? tPause : tPlay)
    }
  }

  // --- Logic ---
  let playerEl = null
  let activeUtterance = null
  let playlist = []
  let currentIndex = -1

  function getPlayer() {
    if (playerEl) return playerEl

    const el = document.createElement('div')
    el.className = 'a11y-ad-player'
    el.innerHTML = renderPlayerContent()
    document.body.appendChild(el)

    // Event Listeners
    el.querySelector('.a11y-ad-close').addEventListener('click', closePlayer)
    el.querySelector('#a11y-ad-prev').addEventListener('click', playPrev)
    el.querySelector('#a11y-ad-next').addEventListener('click', playNext)
    el.querySelector('#a11y-ad-play-pause').addEventListener(
      'click',
      togglePlayPause,
    )
    el.querySelector('#a11y-ad-replay').addEventListener('click', replayCurrent)

    playerEl = el
    return el
  }

  function renderPlayerContent() {
    const tClose = getTranslation('close', 'Kapat')
    const tTitle = getTranslation('audioDescriptionBtn', 'Sesli Oku')

    return `
      <div class="a11y-ad-header">
        <div class="a11y-ad-icon-animation">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
        </div>
        <span id="a11y-ad-title" style="font-weight:600; font-size:14px;">${tTitle}</span>
        <button class="a11y-ad-close" title="${tClose}">
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </div>
      <div class="a11y-ad-content">
        <div class="a11y-ad-text-display" id="a11y-ad-text"></div>
        <div class="a11y-ad-controls">
            <!-- Prev -->
            <button class="a11y-ad-btn" id="a11y-ad-prev" title="${getTranslation('previous', 'Önceki')}">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="19 20 9 12 19 4 19 20"></polygon><line x1="5" y1="19" x2="5" y2="5"></line></svg>
            </button>

            <!-- Replay -->
             <button class="a11y-ad-btn" id="a11y-ad-replay" title="${getTranslation('replay', 'Tekrarla')}">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"></polyline><polyline points="23 20 23 14 17 14"></polyline><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path></svg>
            </button>

            <!-- Play/Pause -->
            <button class="a11y-ad-btn a11y-ad-btn-lg" id="a11y-ad-play-pause" title="${getTranslation('play', 'Oynat')}">
                <svg id="a11y-ad-icon-play" style="display:none;" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                <svg id="a11y-ad-icon-pause" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
            </button>

             <!-- Next -->
            <button class="a11y-ad-btn" id="a11y-ad-next" title="${getTranslation('next', 'Sonraki')}">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 4 15 12 5 20 5 4"></polygon><line x1="19" y1="5" x2="19" y2="19"></line></svg>
            </button>
        </div>
      </div>
    `
  }

  // --- TTS Engine ---

  function stopSpeaking() {
    if (
      window.speechSynthesis &&
      (window.speechSynthesis.speaking || window.speechSynthesis.paused)
    ) {
      window.speechSynthesis.cancel()
    }
  }

  function speak(text) {
    stopSpeaking()

    if (
      !text ||
      !window.speechSynthesis ||
      typeof window.SpeechSynthesisUtterance !== 'function'
    ) {
      return
    }

    activeUtterance = new window.SpeechSynthesisUtterance(text)

    // Language Mock/Detection
    let lang = 'tr-TR' // Default
    if (window.a11yI18n) {
      const map = {
        tr: 'tr-TR',
        en: 'en-US',
        ar: 'ar-SA', // Basic logic
      }
      const current = window.a11yI18n.getCurrentLang()
      if (map[current]) lang = map[current]
    }

    activeUtterance.lang = lang

    // Update Icons on End
    activeUtterance.onend = () => {
      updatePlayPauseIcon(false) // Show Play
    }

    window.speechSynthesis.speak(activeUtterance)
    updatePlayPauseIcon(true) // Show Pause
  }

  function updatePlayPauseIcon(isPlaying) {
    const player = getPlayer()
    const playIcon = player.querySelector('#a11y-ad-icon-play')
    const pauseIcon = player.querySelector('#a11y-ad-icon-pause')

    if (isPlaying) {
      playIcon.style.display = 'none'
      pauseIcon.style.display = 'block'
    } else {
      playIcon.style.display = 'block'
      pauseIcon.style.display = 'none'
    }
  }

  // --- DOM & Navigation ---

  function buildPlaylist() {
    // Scan accessible text elements
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: function (node) {
          if (
            [
              'P',
              'H1',
              'H2',
              'H3',
              'H4',
              'H5',
              'H6',
              'LI',
              'BLOCKQUOTE',
              'SPAN',
              'DIV',
            ].includes(node.tagName)
          ) {
            if (
              node.innerText.trim().length > 3 &&
              node.offsetParent !== null
            ) {
              // Visible and meaningful
              // Avoid nested duplicates (simple check)
              if (
                node.children.length > 0 &&
                Array.from(node.children).some(c =>
                  ['P', 'DIV'].includes(c.tagName),
                )
              ) {
                return NodeFilter.FILTER_SKIP
              }
              return NodeFilter.FILTER_ACCEPT
            }
          }
          return NodeFilter.FILTER_SKIP
        },
      },
    )

    playlist = []
    let currentNode
    while ((currentNode = walker.nextNode())) {
      // Exclude widget itself
      if (
        currentNode.closest('#a11y-widget-host') ||
        currentNode.closest('.a11y-ad-player')
      )
        continue
      playlist.push(currentNode)
    }
  }

  function highlightItem(element) {
    // Remove old highlights
    document
      .querySelectorAll('.a11y-ad-highlight')
      .forEach(el => el.classList.remove('a11y-ad-highlight'))

    if (element) {
      element.classList.add('a11y-ad-highlight')
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  function activateItem(element) {
    if (!element) return

    // Find index
    const index = playlist.indexOf(element)
    if (index !== -1) currentIndex = index

    // UI Updates
    highlightItem(element)
    const text = element.innerText.trim()

    const player = getPlayer()
    player.querySelector('#a11y-ad-text').textContent = text
    player.classList.add('visible')

    // Speak
    speak(text)

    // Notify other players to close
    document.dispatchEvent(new CustomEvent('a11y-ad-activated'))

    // Update Button States
    player.querySelector('#a11y-ad-prev').disabled = currentIndex <= 0
    player.querySelector('#a11y-ad-next').disabled =
      currentIndex >= playlist.length - 1
  }

  function playNext() {
    if (currentIndex < playlist.length - 1) {
      currentIndex++
      activateItem(playlist[currentIndex])
    }
  }

  function playPrev() {
    if (currentIndex > 0) {
      currentIndex--
      activateItem(playlist[currentIndex])
    }
  }

  function replayCurrent() {
    if (currentIndex !== -1 && playlist[currentIndex]) {
      speak(playlist[currentIndex].innerText.trim())
    }
  }

  function togglePlayPause() {
    if (window.speechSynthesis.speaking) {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume()
        updatePlayPauseIcon(true)
      } else {
        window.speechSynthesis.pause()
        updatePlayPauseIcon(false)
      }
    } else {
      replayCurrent()
    }
  }

  function closePlayer() {
    stopSpeaking()
    getPlayer().classList.remove('visible')
    highlightItem(null)
    currentIndex = -1
  }

  // --- Interaction Global Listeners ---

  function handleGlobalMouseOver(e) {
    if (
      e.target.closest('#a11y-widget-host') ||
      e.target.closest('.a11y-ad-player')
    )
      return

    // Simple hover effect
    e.target.classList.add('a11y-ad-hover')
    e.stopPropagation()
  }

  function handleGlobalMouseOut(e) {
    e.target.classList.remove('a11y-ad-hover')
  }

  function handleGlobalClick(e) {
    if (
      e.target.closest('#a11y-widget-host') ||
      e.target.closest('.a11y-ad-player')
    )
      return

    // Try to match clicked element to playlist or find closest parent in playlist
    let target = e.target
    let found = null

    // Look up until we find something in our playlist or hit body
    while (target && target !== document.body) {
      if (playlist.includes(target)) {
        found = target
        break
      }
      target = target.parentElement
    }

    if (found) {
      e.preventDefault()
      e.stopPropagation()
      activateItem(found)
    }
  }

  // --- Feature Toggle ---

  function enableFeature() {
    document.body.classList.add('a11y-ad-enabled')
    injectStyles()
    buildPlaylist()

    document.addEventListener('mouseover', handleGlobalMouseOver)
    document.addEventListener('mouseout', handleGlobalMouseOut)
    // Event Listeners for mutual exclusion
    document.addEventListener('a11y-sl-activated', closePlayer)

    document.addEventListener('click', handleGlobalClick, true) // Capture phase to beat links
  }

  function disableFeature() {
    document.body.classList.remove('a11y-ad-enabled')
    closePlayer()
    removeStyles()

    document.removeEventListener('mouseover', handleGlobalMouseOver)
    document.removeEventListener('mouseout', handleGlobalMouseOut)
    document.removeEventListener('click', handleGlobalClick, true)
    document.removeEventListener('a11y-sl-activated', closePlayer)

    if (playerEl) {
      playerEl.remove()
      playerEl = null
    }
  }

  function toggleState(state) {
    const supported =
      Boolean(window.speechSynthesis) &&
      typeof window.SpeechSynthesisUtterance === 'function'
    CURRENT_AUDIO_DESC_STATE = Boolean(state && supported)
    if (CURRENT_AUDIO_DESC_STATE) {
      enableFeature()
    } else {
      disableFeature()
    }
    toggleFeature(button, CURRENT_AUDIO_DESC_STATE)
  }

  // --- Initialization ---

  const handleAudioDesc = function () {
    const newState = !CURRENT_AUDIO_DESC_STATE
    localStorage.setItem('a11y-audio-desc', newState.toString())
    toggleState(newState)
  }

  initButton(button, handleAudioDesc)

  const buttonElement = root.getElementById(button.id)
  const supported =
    Boolean(window.speechSynthesis) &&
    typeof window.SpeechSynthesisUtterance === 'function'
  if (!supported && buttonElement) {
    buttonElement.disabled = true
    buttonElement.setAttribute('aria-disabled', 'true')
  }

  const savedState = localStorage.getItem('a11y-audio-desc')
  if (savedState === 'true' && supported) toggleState(true)

  registerReset(() => {
    toggleState(false)
  })
  if (runtime.registerRefresh) {
    runtime.registerRefresh(() => {
      if (CURRENT_AUDIO_DESC_STATE) buildPlaylist()
    })
  }
  if (runtime.registerCleanup) {
    runtime.registerCleanup(() => toggleState(false))
  }
}

let CURRENT_FOCUS_INDICATOR_STATE = false

function focusIndicator(root, initButton, toggleFeature, registerReset) {
  const button = {
    name: 'Odak Görünürlüğü',
    icon: `
      <svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9"
          stroke="currentColor" stroke-width="2" fill="none"/>
        <circle cx="12" cy="12" r="4"
          stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3"
          stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `,
    id: 'focusIndicatorBtn',
    type: 'button',
  }

  const handleFocusIndicator = function () {
    CURRENT_FOCUS_INDICATOR_STATE = !CURRENT_FOCUS_INDICATOR_STATE
    localStorage.setItem('a11y-focus-indicator', CURRENT_FOCUS_INDICATOR_STATE)
    setFocusIndicator(CURRENT_FOCUS_INDICATOR_STATE)
  }

  function setFocusIndicator(currentState) {
    let styleEl = document.getElementById('a11y-focus-indicator-style')

    if (currentState) {
      if (!styleEl) {
        styleEl = prepareWidgetStyle(document.createElement('style'))
        styleEl.id = 'a11y-focus-indicator-style'
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        /* Temel odak stilleri - Daha belirgin ve profesyonel */
        *:focus {
          outline: 4px solid #FF3B30 !important;
          outline-offset: 3px !important;
          box-shadow:
            0 0 0 8px rgba(255, 59, 48, 0.4),
            0 0 20px rgba(255, 59, 48, 0.6) !important;
          border-radius: 4px !important;
          position: relative !important;
          z-index: 9999 !important;
        }

        /* Butonlar için - Mavi ton */
        button:focus,
        [role="button"]:focus,
        [type="button"]:focus,
        [type="submit"]:focus {
          outline: 5px solid #007AFF !important;
          outline-offset: 4px !important;
          box-shadow:
            0 0 0 10px rgba(0, 122, 255, 0.4),
            0 0 25px rgba(0, 122, 255, 0.7),
            inset 0 0 10px rgba(255, 255, 255, 0.5) !important;
          transform: translateY(-2px) !important;
          transition: all 0.2s ease !important;
        }

        /* Linkler için - Mor ton */
        a:focus,
        [role="link"]:focus {
          outline: 4px solid #5856D6 !important;
          outline-offset: 3px !important;
          box-shadow:
            0 0 0 8px rgba(88, 86, 214, 0.4),
            0 0 15px rgba(88, 86, 214, 0.6) !important;
          text-decoration: underline !important;
          text-decoration-thickness: 3px !important;
          text-underline-offset: 4px !important;
        }

        /* Form elementleri için - Yeşil ton */
        input:focus,
        select:focus,
        textarea:focus {
          outline: 4px solid #34C759 !important;
          outline-offset: 2px !important;
          box-shadow:
            0 0 0 6px rgba(52, 199, 89, 0.4),
            0 0 15px rgba(52, 199, 89, 0.6),
            inset 0 0 8px rgba(52, 199, 89, 0.2) !important;
          border-color: #34C759 !important;
        }

        /* Tab ve navigasyon elementleri için - Turuncu ton */
        [role="tab"]:focus,
        [role="menuitem"]:focus,
        .tab:focus,
        .nav-item:focus {
          outline: 4px solid #FF9500 !important;
          outline-offset: 3px !important;
          box-shadow:
            0 0 0 8px rgba(255, 149, 0, 0.4),
            0 0 20px rgba(255, 149, 0, 0.6) !important;
        }

        /* Özel durum: Skip to content link'i */
        .skip-link:focus,
        [href="#main-content"]:focus {
          outline: 5px solid #FF2D55 !important;
          outline-offset: 4px !important;
          box-shadow:
            0 0 0 12px rgba(255, 45, 85, 0.5),
            0 0 30px rgba(255, 45, 85, 0.8) !important;
          background: #FF2D55 !important;
          color: white !important;
        }

        /* Animasyon ekleme - dikkat çekici puls efekti */
        @keyframes a11y-focus-pulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(255, 59, 48, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0); }
        }

        /* Sadece klavye odağı için puls efekti (fare ile tıklamada yok) */
        *:focus-visible {
          animation: a11y-focus-pulse 1.5s infinite !important;
        }

        /* İçerik alanları için */
        [role="main"]:focus,
        [role="article"]:focus,
        main:focus,
        article:focus {
          outline: 6px solid #5AC8FA !important;
          outline-offset: 4px !important;
          box-shadow:
            0 0 0 15px rgba(90, 200, 250, 0.3),
            0 0 30px rgba(90, 200, 250, 0.5) !important;
        }
      `
    } else {
      if (styleEl) styleEl.remove()
    }

    toggleFeature(button, currentState)
  }

  handleFocusIndicator.setPreference = state => {
    localStorage.setItem('a11y-focus-indicator', String(Boolean(state)))
    setFocusIndicator(Boolean(state))
  }
  initButton(button, handleFocusIndicator)

  function control() {
    let option = localStorage.getItem('a11y-focus-indicator')
    if (option !== null) {
      CURRENT_FOCUS_INDICATOR_STATE = option === 'true'
      setFocusIndicator(CURRENT_FOCUS_INDICATOR_STATE)
    }
  }
  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_FOCUS_INDICATOR_STATE = false
    setFocusIndicator(false)
  })
}

const FONT_FAMILY_VALUES = [
  'reset',
  'Arial, sans-serif',
  'Verdana, sans-serif',
  'Georgia, serif',
  '"Courier New", monospace',
]

let CURRENT_FONT_FAMILY_LEVEL = 0

function fontFamily(root, initButton, cycleFeature, registerReset) {
  const button = {
    name: 'Yazı Tipi',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M9 4v3h5v12h3V7h5V4H9zm-6 8h3v7h3v-7h3V9H3v3z"/>
          </svg>`,
    id: 'fontFamilyBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: FONT_FAMILY_VALUES.length - 1,
      currentLevel: 0,
    },
  }

  const handleFontFamily = function () {
    CURRENT_FONT_FAMILY_LEVEL =
      (CURRENT_FONT_FAMILY_LEVEL + 1) % (button.cycleOptions.maxLevel + 1)
    localStorage.setItem('a11y-font-family', CURRENT_FONT_FAMILY_LEVEL)
    setFontFamily(CURRENT_FONT_FAMILY_LEVEL)
  }

  function setFontFamily(currentLevel) {
    let styleEl = document.getElementById('a11y-font-family-style')

    if (styleEl) {
      styleEl.remove()
    }

    if (currentLevel > 0) {
      const fontFamilyValue = FONT_FAMILY_VALUES[currentLevel]
      styleEl = prepareWidgetStyle(document.createElement('style'))
      styleEl.id = 'a11y-font-family-style'
      styleEl.textContent = `
      body, body *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *):not(.fa):not(.fas):not(.far):not(.fab):not(.material-icons):not([class*="icon"]):not([class*="Icon"]):not(.glyphicon):not([class*="bi-"]):not([class*="ti-"]) {
        font-family: ${fontFamilyValue} !important;
      }
    `
      document.head.appendChild(styleEl)
    }

    cycleFeature(button, currentLevel)
  }

  handleFontFamily.setPreference = level => {
    localStorage.setItem('a11y-font-family', String(level))
    setFontFamily(Number(level))
  }
  initButton(button, handleFontFamily)

  function control() {
    let option = localStorage.getItem('a11y-font-family')
    if (option !== null) {
      CURRENT_FONT_FAMILY_LEVEL = parseInt(option)
      setFontFamily(CURRENT_FONT_FAMILY_LEVEL)
    }
  }

  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_FONT_FAMILY_LEVEL = 0
    setFontFamily(0)
  })
}

const FONT_STEP = 5
let CURRENT_FONT_SIZE_LEVEL = 0

function fontSize(
  root,
  initButton,
  cycleFeature,
  registerReset,
  runtime = {},
) {
  const TEXT_TAGS =
    'h1, h2, h3, h4, h5, h6, p, a, span, li, td, th, blockquote, label, button, input, textarea, cite, caption, small, b, i, strong, em'
  const touchedElements = new Set()
  const originalStyles = new WeakMap()

  const button = {
    name: 'Yazı Boyutu',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 4v3h5v12h3V7h5V4H9zm-6 8h3v7h3v-7h3V9H3v3z" /></svg>`,
    id: 'fontSizeBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: 4,
      currentLevel: 0,
    },
  }

  function rememberElement(element) {
    if (originalStyles.has(element)) {
      touchedElements.add(element)
      return originalStyles.get(element)
    }

    const computedSize = Number.parseFloat(window.getComputedStyle(element).fontSize)
    if (!Number.isFinite(computedSize)) return null

    const original = {
      computedSize,
      inlineValue: element.style.getPropertyValue('font-size'),
      inlinePriority: element.style.getPropertyPriority('font-size'),
    }
    originalStyles.set(element, original)
    touchedElements.add(element)
    return original
  }

  function restoreElement(element) {
    const original = originalStyles.get(element)
    if (!original || !element.isConnected) return

    if (original.inlineValue) {
      element.style.setProperty(
        'font-size',
        original.inlineValue,
        original.inlinePriority,
      )
    } else {
      element.style.removeProperty('font-size')
    }
    element.removeAttribute('data-a11y-font-size')
    originalStyles.delete(element)
  }

  function restoreAll() {
    touchedElements.forEach(restoreElement)
    touchedElements.clear()
  }

  function applyToDocument() {
    if (CURRENT_FONT_SIZE_LEVEL <= 0) return

    const multiplier = 1 + CURRENT_FONT_SIZE_LEVEL * (FONT_STEP / 100)
    document.querySelectorAll(TEXT_TAGS).forEach(element => {
      if (element.closest('[data-accessibility-preference-widget]')) return
      const original = rememberElement(element)
      if (!original) return

      element.style.setProperty(
        'font-size',
        `${original.computedSize * multiplier}px`,
      )
      element.setAttribute(
        'data-a11y-font-size',
        String(CURRENT_FONT_SIZE_LEVEL),
      )
    })
  }

  function setLevel(level) {
    const normalized = Number.isFinite(level)
      ? Math.min(Math.max(Math.trunc(level), 0), button.cycleOptions.maxLevel)
      : 0

    if (normalized === 0) restoreAll()
    CURRENT_FONT_SIZE_LEVEL = normalized

    if (normalized > 0) applyToDocument()
    cycleFeature(button, normalized)
  }

  function handleFontSize() {
    const nextLevel =
      (CURRENT_FONT_SIZE_LEVEL + 1) % (button.cycleOptions.maxLevel + 1)
    localStorage.setItem('a11y-font-size', String(nextLevel))
    setLevel(nextLevel)
  }
  handleFontSize.setPreference = level => {
    localStorage.setItem('a11y-font-size', String(level))
    setLevel(level)
  }

  initButton(button, handleFontSize)

  const savedLevel = Number.parseInt(localStorage.getItem('a11y-font-size'), 10)
  if (Number.isFinite(savedLevel)) setLevel(savedLevel)

  registerReset(() => setLevel(0))
  if (runtime.registerRefresh) runtime.registerRefresh(applyToDocument)
}

let CURRENT_HIDE_IMAGES_STATE = false

function hideImages(
  root,
  initButton,
  toggleFeature,
  registerReset,
  runtime = {},
) {
  const button = {
    name: 'Resimleri Gizle',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M21 3H3C2 3 1 4 1 5v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1-1-2-2-2zM5 17l3.5-4.5 2.5 3 3.5-4.5 4.5 6H5z"/>
          </svg>`,
    id: 'hideImagesBtn',
    type: 'button',
  }

  const hiddenImages = new Set()
  const originalStyles = new WeakMap()

  function hideCurrentImages() {
    if (!CURRENT_HIDE_IMAGES_STATE) return

    document.querySelectorAll('img').forEach(image => {
      if (
        image.closest('[data-accessibility-preference-widget]') ||
        originalStyles.has(image)
      ) {
        return
      }

      originalStyles.set(image, {
        value: image.style.getPropertyValue('display'),
        priority: image.style.getPropertyPriority('display'),
      })
      hiddenImages.add(image)
      image.style.setProperty('display', 'none', 'important')
      image.setAttribute('data-a11y-image-hidden', '')
    })
  }

  function restoreImages() {
    hiddenImages.forEach(image => {
      const original = originalStyles.get(image)
      if (image.isConnected && original) {
        if (original.value) {
          image.style.setProperty('display', original.value, original.priority)
        } else {
          image.style.removeProperty('display')
        }
        image.removeAttribute('data-a11y-image-hidden')
      }
      originalStyles.delete(image)
    })
    hiddenImages.clear()
  }

  function setHideImages(currentState) {
    CURRENT_HIDE_IMAGES_STATE = Boolean(currentState)
    if (CURRENT_HIDE_IMAGES_STATE) hideCurrentImages()
    else restoreImages()
    toggleFeature(button, CURRENT_HIDE_IMAGES_STATE)
  }

  function handleHideImages() {
    const nextState = !CURRENT_HIDE_IMAGES_STATE
    localStorage.setItem('a11y-hide-images', String(nextState))
    setHideImages(nextState)
  }
  handleHideImages.setPreference = state => {
    localStorage.setItem('a11y-hide-images', String(Boolean(state)))
    setHideImages(state)
  }

  initButton(button, handleHideImages)

  const savedState = localStorage.getItem('a11y-hide-images')
  if (savedState !== null) setHideImages(savedState === 'true')

  registerReset(() => setHideImages(false))
  if (runtime.registerRefresh) runtime.registerRefresh(hideCurrentImages)
}

let CURRENT_HIGH_CONTRAST_LEVEL = 0 // 0 = kapalı, 1 = koyu, 2 = sıcak, 3 = açık, 4 = mavi

function highContrast(root, initButton, cycleFeature, registerReset) {
  const modes = [
    {
      id: 'off',
      name: 'Kapalı',
      bodyClass: '',
      displayName: 'Yüksek Kontrast',
    },
    {
      id: 'dark',
      name: 'Koyu Kontrast',
      bodyClass: 'a11y-contrast-dark',
      displayName: 'Koyu Kontrast',
    },
    {
      id: 'warm',
      name: 'Sıcak Kontrast',
      bodyClass: 'a11y-contrast-warm',
      displayName: 'Sıcak Kontrast',
    },
    {
      id: 'light',
      name: 'Açık Kontrast',
      bodyClass: 'a11y-contrast-light',
      displayName: 'Açık Kontrast',
    },
    {
      id: 'blue',
      name: 'Mavi Kontrast',
      bodyClass: 'a11y-contrast-blue',
      displayName: 'Mavi Kontrast',
    },
  ]

  const button = {
    name: 'Yüksek Kontrast',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c1.03.13 2 .45 2.87.93H13v-.93zM13 7h5.24c.25.31.48.65.68 1H13V7zm0 3h6.74c.08.33.15.66.19 1H13v-1zm0 9.93V19h2.87c-.87.48-1.84.8-2.87.93zM18.24 17H13v-1h5.92c-.2.35-.43.69-.68 1zm1.5-3H13v-1h6.93c-.04.34-.11.67-.19 1z"/>
          </svg>`,
    id: 'highContrastBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: modes.length - 1,
      currentLevel: 0,
    },
  }

  // Tüm kontrast sınıflarını temizle
  function clearContrastClasses() {
    modes.forEach(mode => {
      if (mode.bodyClass) {
        document.body.classList.remove(mode.bodyClass)
      }
    })
  }

  // Buton adını güncelle
  // Buton adını güncelleme fonksiyonu KALDIRILDI
  // Kullanıcı isteği üzerine artık buton adı değişmeyecek (Sadece "Kontrast" kalacak)
  // function updateButtonName(level) { ... }

  // Seviyeyi uygula
  function setHighContrast(level) {
    // Geçerli bir seviye mi kontrol et
    if (isNaN(level) || level < 0 || level >= modes.length) {
      level = 0
    }

    CURRENT_HIGH_CONTRAST_LEVEL = level
    localStorage.setItem('a11y-high-contrast-level', level.toString())

    // Tüm kontrast sınıflarını temizle
    clearContrastClasses()

    // Eğer seviye 0 değilse, ilgili sınıfı ekle
    const mode = modes[level]
    if (mode.bodyClass) {
      document.body.classList.add(mode.bodyClass)
    }

    // UI'ı güncelle
    cycleFeature(button, level)
    // updateButtonName(level) // ARTIK ÇAĞRILMIYOR
  }

  // CSS stillerini ekle
  function ensureContrastStyle() {
    let styleEl = document.getElementById('a11y-high-contrast-style')
    if (!styleEl) {
      styleEl = prepareWidgetStyle(document.createElement('style'))
      styleEl.id = 'a11y-high-contrast-style'
      document.head.appendChild(styleEl)

      styleEl.textContent = `
        /* Invert Colors - Renkleri ters çevir */
        body.a11y-contrast-invert {
          filter: invert(1) hue-rotate(180deg) !important;
        }

        /* ÖNEMLİ DÜZELTME: Widget'ı invert etkisinden hariç tut ve layout bozulmasını önle */
        /* Bu kural, body'ye uygulanan filter'ı widget elemanlarında nötrler. */
        body.a11y-contrast-invert #a11y-widget-trigger,
        body.a11y-contrast-invert #a11y-widget-panel {
          /* Layout sorununu önlemek için yığınlama bağlamı oluşturulur */
          isolation: isolate !important;
          z-index: 9999999 !important;
          /* Filtreyi tamamen kaldır ve manuel renkleri uygula */
          filter: none !important;
          background: #FFFFFF !important;
          border-color: #333333 !important;
          color: #000000 !important;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.5) !important;
        }

        body.a11y-contrast-invert #a11y-widget-panel *,
        body.a11y-contrast-invert #a11y-widget-trigger * {
            color: #000000 !important;
            background-color: transparent !important;
            border-color: #000000 !important;
            filter: none !important;
        }

        /* Resimler ve videolar için invert'i kaldır */
        body.a11y-contrast-invert img,
        body.a11y-contrast-invert video,
        body.a11y-contrast-invert iframe,
        body.a11y-contrast-invert canvas {
          filter: invert(1) hue-rotate(180deg) !important;
        }

        /* 1. 🌑 Koyu Kontrast (Dark Contrast) - Daha yumuşak siyah ve canlı sarı */
        body.a11y-contrast-dark,
        body.a11y-contrast-dark *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
          background: #121212 !important; /* Yumuşak Koyu Gri */
          color: #FFFFFF !important; /* Saf Beyaz Metin */
          border-color: #3D3D3D !important;
          box-shadow: none !important;
          background-image: none !important;
        }

        body.a11y-contrast-dark a,
        body.a11y-contrast-dark [href],
        body.a11y-contrast-dark [role="link"] {
          color: #FFEB3B !important; /* Canlı Parlak Sarı */
          text-decoration: underline !important;
          text-decoration-thickness: 2px !important;
          text-underline-offset: 4px !important;
          background-color: transparent !important; /* Arka planı kaldır */
          padding: 0 !important;
          border-radius: 0 !important;
        }

        body.a11y-contrast-dark a:hover {
            color: #FFFFFF !important;
            background-color: #303030 !important;
        }

        body.a11y-contrast-dark button,
        body.a11y-contrast-dark [type="button"],
        body.a11y-contrast-dark [type="submit"],
        body.a11y-contrast-dark [role="button"] {
          background: #3D3D3D !important; /* Koyu Gri Buton Arka planı */
          color: #FFEB3B !important; /* Sarı Metin */
          border: 2px solid #FFEB3B !important;
          font-weight: bold !important;
          text-shadow: none !important;
          border-radius: 4px !important;
        }

        body.a11y-contrast-dark h1,
        body.a11y-contrast-dark h2,
        body.a11y-contrast-dark h3,
        body.a11y-contrast-dark h4,
        body.a11y-contrast-dark h5,
        body.a11y-contrast-dark h6 {
          color: #8C9EFF !important; /* Açık Mavi Başlık */
          text-shadow: none !important;
          border-bottom: 2px solid #3D3D3D !important;
          padding-bottom: 8px !important;
          margin-bottom: 16px !important;
        }

        body.a11y-contrast-dark input,
        body.a11y-contrast-dark select,
        body.a11y-contrast-dark textarea {
          background: #303030 !important;
          color: #FFFFFF !important;
          border: 2px solid #FFEB3B !important; /* Sarı çerçeve */
          border-radius: 4px !important;
        }

        body.a11y-contrast-dark table {
          border: 1px solid #3D3D3D !important;
          width: 100% !important;
        }

        body.a11y-contrast-dark th {
          background: #3D3D3D !important;
          color: #FFFFFF !important;
          border: 1px solid #3D3D3D !important;
        }

        body.a11y-contrast-dark td {
          border: 1px solid #3D3D3D !important;
          padding: 8px !important;
        }

        /* 2. 🔥 Sıcak Kontrast (Warm Contrast) - Daha zengin ve daha az kırmızımsı */
        body.a11y-contrast-warm,
        body.a11y-contrast-warm *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
          background: #201A1B !important; /* Koyu Kahverengi/Kızıl */
          color: #F8F4E3 !important; /* Kremsi Beyaz Metin */
          border-color: #C38865 !important; /* Kahverengi vurgu */
        }

        body.a11y-contrast-warm a,
        body.a11y-contrast-warm [href] {
          color: #FFBB77 !important; /* Açık Turuncu Link */
          text-decoration: underline !important;
          background-color: transparent !important;
          padding: 0 !important;
        }

        body.a11y-contrast-warm button {
          background: #C38865 !important;
          color: #201A1B !important;
          border: 2px solid #FFBB77 !important;
          font-weight: bold !important;
          border-radius: 4px !important;
        }

        body.a11y-contrast-warm h1,
        body.a11y-contrast-warm h2,
        body.a11y-contrast-warm h3 {
          color: #FFBB77 !important;
          border-left: 5px solid #C38865 !important;
          padding-left: 10px !important;
        }

        /* 3. ☀️ Açık Kontrast (Light Contrast) - Maksimum siyah-beyaz ve kalın çerçeveler */
        body.a11y-contrast-light,
        body.a11y-contrast-light *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
          background: #FFFFFF !important;
          color: #000000 !important;
          border-color: #000000 !important;
          box-shadow: none !important;
        }

        body.a11y-contrast-light a,
        body.a11y-contrast-light [href] {
          color: #0000FF !important; /* Mavi linkler (Standart) */
          text-decoration: underline !important;
          text-decoration-thickness: 3px !important; /* Daha kalın alt çizgi */
          text-underline-offset: 4px !important;
          background-color: transparent !important;
          padding: 0 !important;
        }

        body.a11y-contrast-light a:visited {
          color: #800080 !important; /* Mor ziyaret edilmiş link */
        }

        body.a11y-contrast-light button {
          background: #000000 !important;
          color: #FFFFFF !important;
          border: 3px solid #000000 !important;
          font-weight: bold !important;
          border-radius: 0 !important;
        }

        body.a11y-contrast-light input,
        body.a11y-contrast-light select,
        body.a11y-contrast-light textarea {
          background: #FFFFFF !important;
          color: #000000 !important;
          border: 3px solid #000000 !important;
        }

        body.a11y-contrast-light h1,
        body.a11y-contrast-light h2,
        body.a11y-contrast-light h3 {
          color: #000000 !important;
          border-left: 5px solid #000000 !important;
          padding: 8px 8px 8px 12px !important;
          background-color: #F8F8F8 !important;
          border-radius: 0 !important;
        }

        /* 4. 🟦 Mavi Kontrast (Blue Contrast) - Daha derin mavi ve net sarı */
        body.a11y-contrast-blue,
        body.a11y-contrast-blue *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
          background: #00004C !important; /* Derin Lacivert */
          color: #FFFF99 !important; /* Açık Neon Sarı */
          border-color: #4CC4FF !important; /* Açık Mavi Vurgu */
        }

        body.a11y-contrast-blue a,
        body.a11y-contrast-blue [href] {
          color: #4CC4FF !important;
          text-decoration: underline !important;
          background-color: transparent !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }

        body.a11y-contrast-blue button {
          background: #FFFF99 !important;
          color: #00004C !important;
          border: 2px solid #4CC4FF !important;
          font-weight: bold !important;
          text-shadow: none !important;
          border-radius: 4px !important;
        }

        body.a11y-contrast-blue h1,
        body.a11y-contrast-blue h2,
        body.a11y-contrast-blue h3 {
          color: #4CC4FF !important;
          text-shadow: none !important;
          border-bottom: 2px solid #4CC4FF !important;
        }

        body.a11y-contrast-blue input,
        body.a11y-contrast-blue select,
        body.a11y-contrast-blue textarea {
          background: #000066 !important;
          color: #FFFF99 !important;
          border: 2px solid #4CC4FF !important;
        }

        /* Tüm kontrast modları için ortak kurallar (Geliştirildi) */
        body[class*="a11y-contrast-"] .badge,
        body[class*="a11y-contrast-"] .tag,
        body[class*="a11y-contrast-"] .pill {
          font-weight: 700 !important;
          border-width: 2px !important;
          padding: 4px 8px !important;
          border-radius: 50px !important; /* Hap şekli */
        }

        body[class*="a11y-contrast-"] .highlight,
        body[class*="a11y-contrast-"] mark {
          background-color: #FFFF00 !important; /* Evrensel vurgu rengi */
          color: #000000 !important;
          padding: 2px 4px !important;
        }

        body[class*="a11y-contrast-"] code,
        body[class*="a11y-contrast-"] pre {
          background-color: #333333 !important;
          color: #E0E0E0 !important;
          border: 1px solid currentColor !important;
          padding: 10px !important;
          border-radius: 4px !important;
        }

        body[class*="a11y-contrast-"] blockquote {
          border-left: 5px solid currentColor !important;
          padding-left: 15px !important;
          margin-left: 0 !important;
          font-style: italic !important;
          opacity: 0.9 !important;
        }
      `
    }
  }

  // Buton tıklama handler'ı
  const handleHighContrast = function () {
    const nextLevel = (CURRENT_HIGH_CONTRAST_LEVEL + 1) % modes.length
    setHighContrast(nextLevel)
  }
  handleHighContrast.setPreference = level => setHighContrast(Number(level))

  initButton(button, handleHighContrast)
  ensureContrastStyle()

  // Başlangıç kontrolü
  function control() {
    let level = 0

    // Önce yeni anahtarı kontrol et
    const savedLevel = localStorage.getItem('a11y-high-contrast-level')
    if (savedLevel !== null) {
      const parsed = parseInt(savedLevel, 10)
      if (!isNaN(parsed) && parsed >= 0 && parsed < modes.length) {
        level = parsed
      }
    } else {
      // Eski anahtarı kontrol et (boolean)
      const oldValue = localStorage.getItem('a11y-high-contrast')
      if (oldValue !== null) {
        // Eski sistemde true/false idi, true ise koyu moda geç
        level = oldValue === 'true' ? 1 : 0
        // Yeni sisteme geç
        localStorage.removeItem('a11y-high-contrast')
        localStorage.setItem('a11y-high-contrast-level', level.toString())
      }
    }

    CURRENT_HIGH_CONTRAST_LEVEL = level
    setHighContrast(level)
  }

  control()

  // Dil değişimi için global update fonksiyonu KALDIRILDI
  // window.updateHighContrastText = () => { ... }

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    setHighContrast(0)
  })
}

let CURRENT_HIGHLIGHT_LINKS_STATE = false

function highlightLinks(root, initButton, toggleFeature, registerReset) {
  const button = {
    name: 'Bağlantıları Vurgula',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
          </svg>`,
    id: 'highlightLinksBtn',
    type: 'button',
  }

  const handleHighlightLinks = function () {
    CURRENT_HIGHLIGHT_LINKS_STATE = !CURRENT_HIGHLIGHT_LINKS_STATE
    localStorage.setItem('a11y-highlight-links', CURRENT_HIGHLIGHT_LINKS_STATE)
    setHighlightLinks(CURRENT_HIGHLIGHT_LINKS_STATE)
  }

  function setHighlightLinks(currentState) {
    let styleEl = document.getElementById('a11y-highlight-links-style')

    if (currentState) {
      if (!styleEl) {
        styleEl = prepareWidgetStyle(document.createElement('style'))
        styleEl.id = 'a11y-highlight-links-style'
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        a, [role="link"], [onclick] {
          background-color: yellow !important;
          color: black !important;
          text-decoration: underline !important;
          font-weight: bold !important;
        }
      `
    } else {
      if (styleEl) {
        styleEl.remove()
      }
    }

    toggleFeature(button, currentState)
  }

  handleHighlightLinks.setPreference = state => {
    localStorage.setItem('a11y-highlight-links', String(Boolean(state)))
    setHighlightLinks(Boolean(state))
  }
  initButton(button, handleHighlightLinks)

  function control() {
    let option = localStorage.getItem('a11y-highlight-links')
    if (option !== null) {
      CURRENT_HIGHLIGHT_LINKS_STATE = option === 'true'
      setHighlightLinks(CURRENT_HIGHLIGHT_LINKS_STATE)
    }
  }
  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_HIGHLIGHT_LINKS_STATE = false
    setHighlightLinks(false)
  })
}

/**
 * Başlıkları Vurgulama Modülü
 */

let CURRENT_HIGHLIGHT_TITLES_STATE = false

function highlightTitles(root, initButton, toggleFeature, registerReset) {
  const button = {
    id: 'highlightTitlesBtn',
    icon: `<svg class="a11y-card-icon" viewBox="0 0 24 24"><path d="M5 4v3h5.5v12h3V7H19V4z"/></svg>`, // Header icon
    name: 'Başlıkları Vurgula',
    type: 'toggle',
  }

  const handleHighlightTitles = function () {
    CURRENT_HIGHLIGHT_TITLES_STATE = !CURRENT_HIGHLIGHT_TITLES_STATE
    localStorage.setItem(
      'a11y-highlight-titles',
      CURRENT_HIGHLIGHT_TITLES_STATE,
    )
    setHighlightTitles(CURRENT_HIGHLIGHT_TITLES_STATE)
  }

  function setHighlightTitles(state) {
    let styleEl = document.getElementById('a11y-highlight-titles-style')

    if (state) {
      if (!styleEl) {
        styleEl = prepareWidgetStyle(document.createElement('style'))
        styleEl.id = 'a11y-highlight-titles-style'
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        h1, h2, h3, h4, h5, h6,
        [role="heading"] {
          outline: 2px dashed rgba(247, 221, 23, 1) !important;
          outline-offset: 4px !important;
          background-color: rgba(247, 221, 23, 0.45) !important;
          color: inherit !important;
        }
      `
    } else {
      if (styleEl) {
        styleEl.remove()
      }
    }

    toggleFeature(button, state)
  }

  handleHighlightTitles.setPreference = state => {
    localStorage.setItem('a11y-highlight-titles', String(Boolean(state)))
    setHighlightTitles(Boolean(state))
  }
  initButton(button, handleHighlightTitles)

  // Load State
  const saved = localStorage.getItem('a11y-highlight-titles')
  if (saved === 'true') {
    CURRENT_HIGHLIGHT_TITLES_STATE = true
    setHighlightTitles(true)
  }

  // Register Reset
  registerReset(() => {
    CURRENT_HIGHLIGHT_TITLES_STATE = false
    setHighlightTitles(false)
  })
}

/**
 * Erişilebilirlik Dil Seçici Modülü
 */

function languageSelector(root, runtime = {}) {
  // i18n helper
  const tr = key => (window.a11yI18n ? window.a11yI18n.t(key) : key)

  const container = root.querySelector('.a11y-menu-content') // Insert top of menu content not specific container
  // Or create specific container? init.js creates structure.
  // We need a specific root for this. init.js should provide it or we insert into main grid.
  // Best: Create a container inside 'a11y-grid' or just prepend to it.

  // Let's assume we prepend to a11y-grid or a dedicated slot.
  // For now, let's target specific ID created in init.js or create it here.

  let langRoot = root.getElementById('a11y-lang-root')
  if (!langRoot) {
    // Create if missing (inserted before profiles)
    langRoot = document.createElement('div')
    langRoot.id = 'a11y-lang-root'
    langRoot.className = 'a11y-lang-selector-root'

    const profilesRoot = root.getElementById('a11y-profiles-root')
    if (profilesRoot) {
      profilesRoot.parentNode.insertBefore(langRoot, profilesRoot)
    } else {
      // Fallback
      const grid = root.querySelector('.a11y-grid')
      if (grid) grid.prepend(langRoot)
    }
  }

  // Initial Render
  renderLanguageSelector(langRoot, root)

  // Register global updater
  const updateLanguageSelectorUI = () => {
    // Re-render or just update active state?
    // Re-render is safer for text updates "Select Language"
    // But we lose open state.
    // Better: Update texts and active class.
    updateLanguageSelectorState(langRoot)
  }
  window.updateLanguageSelectorUI = updateLanguageSelectorUI

  if (runtime.registerCleanup) {
    runtime.registerCleanup(() => {
      if (window.updateLanguageSelectorUI === updateLanguageSelectorUI) {
        delete window.updateLanguageSelectorUI
      }
    })
  }
}

function renderLanguageSelector(container, root) {
  // i18n
  const tr = key => (window.a11yI18n ? window.a11yI18n.t(key) : key)
  const headerTitle = tr('languageHeader')
  const searchPlaceholder = tr('searchLanguage')
  const languages = window.a11yI18n.getLanguages()
  const currentLang = window.a11yI18n.getCurrentLang()

  // HTML Structure
  container.innerHTML = `
        <button type="button" class="a11y-lang-header" id="a11y-lang-toggle" aria-expanded="false" aria-controls="a11y-lang-content">
            <span class="a11y-disclosure-label">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/></svg>
                <span id="a11y-lang-title">${headerTitle}</span>
            </span>
            <svg class="a11y-lang-toggle-icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M7 10l5 5 5-5z"/></svg>
        </button>
        <div class="a11y-lang-content" id="a11y-lang-content" hidden>
            <div class="a11y-lang-search">
                <input type="text" id="a11y-lang-filter" placeholder="${searchPlaceholder}" aria-label="${searchPlaceholder}">
            </div>
            <div class="a11y-lang-grid" id="a11y-lang-list">
                ${generateLangButtons(languages, currentLang)}
            </div>
        </div>
    `

  // Logic
  const toggle = container.querySelector('#a11y-lang-toggle')
  const content = container.querySelector('#a11y-lang-content')
  const arrow = toggle.querySelector('.a11y-lang-toggle-icon')
  const filterInput = container.querySelector('#a11y-lang-filter')
  const langList = container.querySelector('#a11y-lang-list')

  let isOpen = false

  toggle.addEventListener('click', () => {
    isOpen = !isOpen
    toggle.setAttribute('aria-expanded', String(isOpen))
    content.hidden = !isOpen
    arrow.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
  })

  // Search Listener
  filterInput.addEventListener('input', e => {
    const query = e.target.value.toLowerCase()
    const btns = langList.querySelectorAll('.a11y-lang-btn')
    btns.forEach(btn => {
      const name = btn.textContent.toLowerCase() // Includes flag but ok
      if (name.includes(query)) {
        btn.style.display = 'flex'
      } else {
        btn.style.display = 'none'
      }
    })
  })

  // Click Listeners (Delegation)
  langList.addEventListener('click', e => {
    const btn = e.target.closest('.a11y-lang-btn')
    if (!btn) return

    const code = btn.getAttribute('data-lang')
    window.a11yI18n.setLanguage(code, root)

    // Auto Close? User Request: "direkt seçmeli olsun" implies selection -> action.
    // Maybe close after selection to show result?
    // Let's keep it consistent.
    // isOpen = false
    // content.style.maxHeight = '0'
    // arrow.style.transform = 'rotate(0deg)'
  })
}

function generateLangButtons(languages, currentLang) {
  return languages
    .map(lang => {
      const activeClass = lang.code === currentLang ? 'active' : ''
      return `
            <button class="a11y-lang-btn ${activeClass}" data-lang="${lang.code}">
                <div class="a11y-lang-flag">${lang.flag}</div>
                <div class="a11y-lang-name">${lang.name}</div>
            </button>
        `
    })
    .join('')
}

function updateLanguageSelectorState(container) {
  // i18n
  const tr = key => (window.a11yI18n ? window.a11yI18n.t(key) : key)

  if (!container) return

  // Update active class
  const currentLang = window.a11yI18n.getCurrentLang()
  const btns = container.querySelectorAll('.a11y-lang-btn')
  btns.forEach(btn => {
    if (btn.getAttribute('data-lang') === currentLang) {
      btn.classList.add('active')
    } else {
      btn.classList.remove('active')
    }
  })

  // Update Texts
  const title = container.querySelector('#a11y-lang-title')
  if (title) title.innerText = tr('languageHeader')

  const input = container.querySelector('#a11y-lang-filter')
  if (input) {
    input.placeholder = tr('searchLanguage')
    input.setAttribute('aria-label', tr('searchLanguage'))
  }
}

let CURRENT_LARGE_CURSOR_STATE = false

function largeCursor(root, initButton, toggleFeature, registerReset) {
  const button = {
    name: 'Büyük İmleç',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M13.75 10.19l3.75 3.75-1.48 1.48-3.73-3.73L9 16.94V3h1.5v7.19z"/>
          </svg>`,
    id: 'largeCursorBtn',
    type: 'button',
  }

  const handleLargeCursor = function () {
    CURRENT_LARGE_CURSOR_STATE = !CURRENT_LARGE_CURSOR_STATE
    localStorage.setItem('a11y-large-cursor', CURRENT_LARGE_CURSOR_STATE)
    setLargeCursor(CURRENT_LARGE_CURSOR_STATE)
  }

  function setLargeCursor(currentState) {
    let styleEl = document.getElementById('a11y-large-cursor-style')

    if (currentState) {
      if (!styleEl) {
        styleEl = prepareWidgetStyle(document.createElement('style'))
        styleEl.id = 'a11y-large-cursor-style'
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        body, body * {
          cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="black" stroke="white" stroke-width="2"/></svg>') 16 16, auto !important;
        }

        button, a, [role="button"], input, select, textarea {
          cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="blue" stroke="white" stroke-width="2"/></svg>') 16 16, pointer !important;
        }
      `
    } else {
      if (styleEl) {
        styleEl.remove()
      }
    }

    toggleFeature(button, currentState)
  }

  handleLargeCursor.setPreference = state => {
    localStorage.setItem('a11y-large-cursor', String(Boolean(state)))
    setLargeCursor(Boolean(state))
  }
  initButton(button, handleLargeCursor)

  function control() {
    let option = localStorage.getItem('a11y-large-cursor')
    if (option !== null) {
      CURRENT_LARGE_CURSOR_STATE = option === 'true'
      setLargeCursor(CURRENT_LARGE_CURSOR_STATE)
    }
  }
  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_LARGE_CURSOR_STATE = false
    setLargeCursor(false)
  })
}

const LETTER_SPACING_VALUES = ['normal', '0.1em', '0.2em', '0.3em']
let CURRENT_LETTER_SPACING_LEVEL = 0

function letterSpacing(root, initButton, cycleFeature, registerReset) {
  const button = {
    name: 'Harf Boşluğu',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M6 7h2.5L5 3.5 1.5 7H4v10H1.5L5 20.5 8.5 17H6V7zm4-2v2h12V5H10zm0 14h12v-2H10v2zm0-6h12v-2H10v2z"/>
          </svg>`,
    id: 'letterSpacingBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: 3,
      currentLevel: 0,
    },
  }

  const handleLetterSpacing = function () {
    CURRENT_LETTER_SPACING_LEVEL =
      (CURRENT_LETTER_SPACING_LEVEL + 1) % (button.cycleOptions.maxLevel + 1)
    localStorage.setItem('a11y-letter-spacing', CURRENT_LETTER_SPACING_LEVEL)
    setLetterSpacing(CURRENT_LETTER_SPACING_LEVEL)
  }

  function setLetterSpacing(currentLevel) {
    const letterSpacingValue = LETTER_SPACING_VALUES[currentLevel]

    let styleEl = document.getElementById('a11y-letter-spacing-style')

    // Eğer seviye 0 ise (normal - orijinal durum), style elementini kaldır
    if (currentLevel === 0) {
      if (styleEl) {
        styleEl.remove()
      }
      cycleFeature(button, currentLevel)
      return
    }

    // Style elementini oluştur veya güncelle
    if (!styleEl) {
      styleEl = prepareWidgetStyle(document.createElement('style'))
      styleEl.id = 'a11y-letter-spacing-style'
      document.head.appendChild(styleEl)
    }

    styleEl.textContent = `
      body, body *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
        letter-spacing: ${letterSpacingValue} !important;
        word-spacing: ${letterSpacingValue} !important;
      }
    `

    cycleFeature(button, currentLevel)
  }

  handleLetterSpacing.setPreference = level => {
    localStorage.setItem('a11y-letter-spacing', String(level))
    setLetterSpacing(Number(level))
  }
  initButton(button, handleLetterSpacing)

  function control() {
    let option = localStorage.getItem('a11y-letter-spacing')
    if (option !== null) {
      CURRENT_LETTER_SPACING_LEVEL = parseInt(option)
      setLetterSpacing(CURRENT_LETTER_SPACING_LEVEL)
      // Buton görsel durumunu güncelle
      cycleFeature(button, CURRENT_LETTER_SPACING_LEVEL)
    }
  }

  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_LETTER_SPACING_LEVEL = 0
    setLetterSpacing(0)
  })
}

const LINE_HEIGHT_VALUES = ['normal', '2.0', '2.5', '3.0']
let CURRENT_LINE_HEIGHT_LEVEL = 0

function lineHeight(root, initButton, cycleFeature, registerReset) {
  const button = {
    name: 'Satır Yüksekliği',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M10 13h10v-2H10v2zm0-8v2h10V5H10zm0 12h10v-2H10v2zM6 5v14l-3-3 3-3V5z"/>
          </svg>`,
    id: 'lineHeightBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: 3,
      currentLevel: 0,
    },
  }

  const handleLineHeight = function () {
    CURRENT_LINE_HEIGHT_LEVEL =
      (CURRENT_LINE_HEIGHT_LEVEL + 1) % (button.cycleOptions.maxLevel + 1)
    localStorage.setItem('a11y-line-height', CURRENT_LINE_HEIGHT_LEVEL)
    setLineHeight(CURRENT_LINE_HEIGHT_LEVEL)
  }

  function setLineHeight(currentLevel) {
    const lineHeightValue = LINE_HEIGHT_VALUES[currentLevel]

    let styleEl = document.getElementById('a11y-line-height-style')

    // Eğer seviye 0 ise (normal - orijinal durum), style elementini kaldır
    if (currentLevel === 0) {
      if (styleEl) {
        styleEl.remove()
      }
      cycleFeature(button, currentLevel)
      return
    }

    // Style elementini oluştur veya güncelle
    if (!styleEl) {
      styleEl = prepareWidgetStyle(document.createElement('style'))
      styleEl.id = 'a11y-line-height-style'
      document.head.appendChild(styleEl)
    }

    styleEl.textContent = `
      body, body *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
        line-height: ${lineHeightValue} !important;
      }
    `

    cycleFeature(button, currentLevel)
  }

  handleLineHeight.setPreference = level => {
    localStorage.setItem('a11y-line-height', String(level))
    setLineHeight(Number(level))
  }
  initButton(button, handleLineHeight)

  function control() {
    let option = localStorage.getItem('a11y-line-height')
    if (option !== null) {
      CURRENT_LINE_HEIGHT_LEVEL = parseInt(option)
      setLineHeight(CURRENT_LINE_HEIGHT_LEVEL)
      // Buton görsel durumunu güncelle
      cycleFeature(button, CURRENT_LINE_HEIGHT_LEVEL)
    }
  }

  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_LINE_HEIGHT_LEVEL = 0
    setLineHeight(0)
  })
}

let CURRENT_MONOCHROME_STATE = false

function monochrome(root, initButton, toggleFeature, registerReset) {
  const button = {
    name: 'Monokrom',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            <circle cx="12" cy="12" r="5"/>
          </svg>`,
    id: 'monochromeBtn',
    type: 'button',
  }

  const handleMonochrome = function () {
    CURRENT_MONOCHROME_STATE = !CURRENT_MONOCHROME_STATE
    localStorage.setItem('a11y-monochrome', CURRENT_MONOCHROME_STATE)
    setMonochrome(CURRENT_MONOCHROME_STATE)
  }

  function setMonochrome(currentState) {
    let styleEl = document.getElementById('a11y-monochrome-style')

    if (currentState) {
      if (!styleEl) {
        styleEl = prepareWidgetStyle(document.createElement('style'))
        styleEl.id = 'a11y-monochrome-style'
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        html {
          filter: grayscale(100%) !important;
        }
      `
    } else {
      if (styleEl) {
        styleEl.remove()
      }
    }

    toggleFeature(button, currentState)
  }

  handleMonochrome.setPreference = state => {
    localStorage.setItem('a11y-monochrome', String(Boolean(state)))
    setMonochrome(Boolean(state))
  }
  initButton(button, handleMonochrome)

  function control() {
    let option = localStorage.getItem('a11y-monochrome')
    if (option !== null) {
      CURRENT_MONOCHROME_STATE = option === 'true'
      setMonochrome(CURRENT_MONOCHROME_STATE)
    }
  }
  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_MONOCHROME_STATE = false
    setMonochrome(false)
  })
}

let CURRENT_PAUSE_ANIMATIONS_STATE = false

function pauseAnimations(root, initButton, toggleFeature, registerReset) {
  const button = {
    name: 'Animasyonları Duraklat',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
          </svg>`,
    id: 'pauseAnimationsBtn',
    type: 'button',
  }

  const handlePauseAnimations = function () {
    CURRENT_PAUSE_ANIMATIONS_STATE = !CURRENT_PAUSE_ANIMATIONS_STATE
    localStorage.setItem(
      'a11y-pause-animations',
      CURRENT_PAUSE_ANIMATIONS_STATE,
    )
    setPauseAnimations(CURRENT_PAUSE_ANIMATIONS_STATE)
  }

  function setPauseAnimations(currentState) {
    let styleEl = document.getElementById('a11y-pause-animations-style')

    if (currentState) {
      if (!styleEl) {
        styleEl = prepareWidgetStyle(document.createElement('style'))
        styleEl.id = 'a11y-pause-animations-style'
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        *, *::before, *::after {
          animation-duration: 0s !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0s !important;
          animation-play-state: paused !important;
          transition-property: none !important;
          scroll-behavior: auto !important;
        }

        video, audio, [autoplay] {
          animation: none !important;
          transition: none !important;
        }
      `
    } else {
      if (styleEl) {
        styleEl.remove()
      }
    }

    toggleFeature(button, currentState)
  }

  handlePauseAnimations.setPreference = state => {
    localStorage.setItem('a11y-pause-animations', String(Boolean(state)))
    setPauseAnimations(Boolean(state))
  }
  initButton(button, handlePauseAnimations)

  function control() {
    let option = localStorage.getItem('a11y-pause-animations')
    if (option !== null) {
      CURRENT_PAUSE_ANIMATIONS_STATE = option === 'true'
      setPauseAnimations(CURRENT_PAUSE_ANIMATIONS_STATE)
    }
  }
  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_PAUSE_ANIMATIONS_STATE = false
    setPauseAnimations(false)
  })
}

/**
 * Erişilebilirlik Profilleri Modülü
 */

const PROFILES = [
  {
    id: 'color-blind',
    icon: `<svg viewBox="0 0 24 24"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>`,
    actions: { highContrast: true },
  },
  {
    id: 'dyslexia',
    icon: `<svg viewBox="0 0 24 24"><path d="M9 11l3-3 3 3M12 8v6" stroke="currentColor" stroke-width="2" fill="none"/><text x="12" y="22" font-size="10" text-anchor="middle" font-weight="bold" fill="currentColor">Df</text></svg>`,
    actions: { fontFamily: 2, letterSpacing: 2 },
  },
  {
    id: 'vision-impaired',
    icon: `<svg viewBox="0 0 24 24"><path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" /></svg>`,
    actions: { fontSize: 2, highContrast: true, largeCursor: true },
  },
  {
    id: 'cognitive-disability',
    icon: `<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 6C13.66 6 15 7.34 15 9C15 10.66 13.66 12 12 12C10.34 12 9 10.66 9 9C9 7.34 10.34 6 12 6ZM12 20C9.33 20 7 18 7 15.5V15H17V15.5C17 18 14.67 20 12 20Z" /></svg>`,
    actions: {
      highlightLinks: true,
      readingGuide: true,
      highlightTitles: true,
    },
  },
  {
    id: 'seizure-safe',
    icon: `<svg viewBox="0 0 24 24"><path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" /></svg>`,
    actions: { pauseAnimations: true, monochrome: true },
  },
  {
    id: 'adhd-friendly',
    icon: `<svg viewBox="0 0 24 24"><path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 17H7V15H17V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z" /></svg>`,
    actions: { readingGuide: 2, focusIndicator: true },
  },
]

let ACTIVE_PROFILES = {}

function profiles(root, moduleMethods, registerReset, runtime = {}) {
  // i18n helper (Scope safe)
  const tr = key => (window.a11yI18n ? window.a11yI18n.t(key) : key)

  const container = root.getElementById('a11y-profiles-root')
  if (!container) return

  // Header Ekle (Collapsible Control ile)
  // Leading Icon Eklendi
  const headerHtml = `
    <button
      type="button"
      class="a11y-profiles-header"
      aria-expanded="false"
      aria-controls="a11y-profiles-content"
    >
        <span class="a11y-disclosure-label">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>
            <span>${tr('profilesHeader')}</span>
        </span>
        <svg class="a11y-profiles-toggle-icon" viewBox="0 0 24 24" width="24" height="24" style="transition: transform 0.3s; transform: rotate(-90deg);"><path d="M7 10l5 5 5-5z"/></svg>
    </button>`

  // Profil Grid Container
  // Default kapalı (isOpen=false)
  const listHtml = `
    <div class="a11y-profiles-wrapper">
        ${headerHtml}
        <div
          class="a11y-profiles-content"
          id="a11y-profiles-content"
          hidden
        >
            <div class="a11y-profiles-grid"></div>
        </div>
    </div>
  `
  container.innerHTML = listHtml

  const headerEl = container.querySelector('.a11y-profiles-header')
  const contentEl = container.querySelector('.a11y-profiles-content')
  const toggleIcon = container.querySelector('.a11y-profiles-toggle-icon')

  let isOpen = false
  const handleHeaderClick = () => {
    isOpen = !isOpen
    headerEl.setAttribute('aria-expanded', String(isOpen))
    contentEl.hidden = !isOpen
    toggleIcon.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
  }
  headerEl.addEventListener('click', handleHeaderClick)

  const grid = container.querySelector('.a11y-profiles-grid')

  PROFILES.forEach(profile => {
    // HTML Oluştur (Brutalist Button Yapısı - Custom Class)
    const profileCard = document.createElement('button')
    profileCard.type = 'button'
    profileCard.className = 'a11y-profile-card'
    profileCard.id = `profile-${profile.id}`
    profileCard.setAttribute('aria-pressed', 'false')
    // Style handled in CSS now

    // Icon + Title
    profileCard.innerHTML = `
      <div class="a11y-profile-icon">${profile.icon}</div>
      <div class="a11y-profile-title">
        <span>${tr(profile.id)}</span>
      </div>
    `

    // Event Listener (Tüm kart tıklanabilir)
    profileCard.addEventListener('click', () => {
      toggleProfile(profile.id, root, moduleMethods)
    })

    grid.appendChild(profileCard)
  })

  // Başlangıç durumunu yükle
  loadState(root)

  // Reset logic
  registerReset(() => {
    ACTIVE_PROFILES = {}
    const allCards = container.querySelectorAll('.a11y-profile-card')
    allCards.forEach(card => {
      card.classList.remove('active')
      card.setAttribute('aria-pressed', 'false')
    })
    localStorage.removeItem('a11y-active-profiles')
  })

  if (runtime.registerCleanup) {
    runtime.registerCleanup(() => {
      headerEl.removeEventListener('click', handleHeaderClick)
    })
  }
}

function toggleProfile(profileId, root, moduleMethods) {
  const profile = PROFILES.find(p => p.id === profileId)
  if (!profile) return

  const isActive = !!ACTIVE_PROFILES[profileId]
  const newState = !isActive

  ACTIVE_PROFILES[profileId] = newState
  if (!newState) delete ACTIVE_PROFILES[profileId]

  updateProfileUI(profileId, newState, root)
  applyProfileActions(profile, newState, root, moduleMethods)
  saveState()
}

function updateProfileUI(profileId, isActive, root) {
  const profileCard = root.getElementById(`profile-${profileId}`)
  if (!profileCard) return

  profileCard.classList.toggle('active', isActive)
  profileCard.setAttribute('aria-pressed', isActive ? 'true' : 'false')
}

// Çevirileri güncelleyen fonksiyon
window.updateProfileTexts = function (root) {
  // i18n helper (Fix ReferenceError)
  const tr = key => (window.a11yI18n ? window.a11yI18n.t(key) : key)

  const grid = root.querySelector('.a11y-profiles-grid')
  if (!grid) return

  const headerSpan = root.querySelector('.a11y-profiles-header span')
  if (headerSpan) headerSpan.innerText = tr('profilesHeader')

  PROFILES.forEach(profile => {
    const card = root.getElementById(`profile-${profile.id}`)
    if (card) {
      const span = card.querySelector('.a11y-profile-title span')
      if (span) span.innerText = tr(profile.id)
    }
  })
}

/**
 * Akıllı Profil Uygulayıcı
 * Modülün mevcut durumunu kontrol ederek hedef duruma getirir.
 */
function applyProfileActions(profile, isActive, root, moduleMethods) {
  const actions = profile.actions

  Object.keys(actions).forEach(actionKey => {
    const actionValue = actions[actionKey] // true or number (level)

    // Action Key -> Button ID Mapping
    const buttonMap = {
      fontSize: 'fontSizeBtn',
      highContrast: 'highContrastBtn',
      pauseAnimations: 'pauseAnimationsBtn',
      monochrome: 'monochromeBtn',
      readingGuide: 'readingGuideBtn',
      focusIndicator: 'focusIndicatorBtn',
      highlightLinks: 'highlightLinksBtn',
      highlightTitles: 'highlightTitlesBtn',
      largeCursor: 'largeCursorBtn',
      fontFamily: 'fontFamilyBtn',
      letterSpacing: 'letterSpacingBtn',
      hideImages: 'hideImagesBtn',
      lineHeight: 'lineHeightBtn',
    }

    const btnId = buttonMap[actionKey]
    const handler = moduleMethods[btnId]

    if (!handler) return

    const targetLevel = isActive
      ? typeof actionValue === 'number'
        ? actionValue
        : 1
      : 0

    if (typeof handler.setPreference === 'function') {
      handler.setPreference(
        typeof actionValue === 'number' ? targetLevel : targetLevel > 0,
      )
    }
  })
}

function saveState() {
  localStorage.setItem('a11y-active-profiles', JSON.stringify(ACTIVE_PROFILES))
}

function loadState(root) {
  try {
    const saved = localStorage.getItem('a11y-active-profiles')
    if (saved) {
      ACTIVE_PROFILES = JSON.parse(saved)
      Object.keys(ACTIVE_PROFILES).forEach(id => {
        if (ACTIVE_PROFILES[id]) {
          updateProfileUI(id, true, root)
        }
      })
    }
  } catch {
    ACTIVE_PROFILES = {}
  }
}

/**
 * Okuma Kılavuzu Modülü (Gelişmiş - Line & Mask)
 */

let CURRENT_READING_GUIDE_LEVEL = 0 // 0: Off, 1: Line, 2: Mask
let readingGuideElement = null
let mouseMoveHandler = null
let touchMoveHandler = null

function readingGuide(root, initButton, cycleFeature, registerReset) {
  const button = {
    name: 'Okuma Kılavuzu',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M3 17h18c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0-4h18c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0-4h18c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1z"/>
            </svg>`,
    id: 'readingGuideBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: 2, // 1: Line, 2: Mask
    },
  }

  const handleReadingGuide = function () {
    CURRENT_READING_GUIDE_LEVEL = (CURRENT_READING_GUIDE_LEVEL + 1) % 3
    localStorage.setItem(
      'a11y-reading-guide-level',
      CURRENT_READING_GUIDE_LEVEL,
    )
    setReadingGuide(CURRENT_READING_GUIDE_LEVEL)
  }

  function createReadingGuide() {
    if (readingGuideElement) return readingGuideElement

    readingGuideElement = document.createElement('div')
    readingGuideElement.id = 'a11y-reading-guide'
    // Default styles will be applied based on mode
    readingGuideElement.style.cssText = `
            position: fixed;
            left: 0;
            width: 100%;
            pointer-events: none;
            z-index: 999999;
            display: none;
            transition: top 0.05s linear;
        `
    document.body.appendChild(readingGuideElement)
    return readingGuideElement
  }

  function updateGuideStyle(level) {
    const guide = readingGuideElement || createReadingGuide()

    if (level === 1) {
      // LINE MODE
      guide.style.height = '5px'
      guide.style.background = '#ff4444'
      guide.style.boxShadow = '0 0 15px rgba(255, 68, 68, 1)'
    } else if (level === 2) {
      // MASK MODE
      // Box shadow trick: Transparent center, huge dark shadow around
      guide.style.height = '60px' // Reading strip height
      guide.style.background = 'transparent'
      guide.style.boxShadow = '0 0 0 100vh rgba(0, 0, 0, 0.75)'
    }
  }

  function handleMove(e) {
    const guide = readingGuideElement || createReadingGuide()

    let clientY
    if (e.type === 'scroll') {
      if (e.clientY === undefined) {
        if (!guide.lastClientY) return
        clientY = guide.lastClientY
      } else {
        clientY = e.clientY
        guide.lastClientY = clientY
      }
    } else {
      clientY =
        e.clientY !== undefined
          ? e.clientY
          : e.touches && e.touches.length > 0
          ? e.touches[0].clientY
          : null
      if (clientY === null) return
      guide.lastClientY = clientY
    }

    if (clientY === undefined) return

    guide.style.display = 'block'

    // Position calculation depends on mode
    if (CURRENT_READING_GUIDE_LEVEL === 1) {
      // Line: top at cursor
      guide.style.top = clientY + 'px'
    } else if (CURRENT_READING_GUIDE_LEVEL === 2) {
      // Mask: center around cursor
      // Center is cursor, so top is clientY - height/2
      guide.style.top = clientY - 30 + 'px'
    }
  }

  function setReadingGuide(level) {
    const guide = readingGuideElement || createReadingGuide()

    // Cycle Feature Update
    cycleFeature(button, level)

    if (level > 0) {
      updateGuideStyle(level)

      // Add Listeners
      if (!mouseMoveHandler) {
        mouseMoveHandler = handleMove
        document.addEventListener('mousemove', mouseMoveHandler)
        document.addEventListener('touchmove', handleMove, { passive: true })
        window.addEventListener('scroll', handleMove)
      }
      guide.style.display = 'block'

      // If we have a last position, verify it matches the new style's layout
      if (guide.lastClientY) {
        // Re-trigger move to update position based on new mode logic
        handleMove({ clientY: guide.lastClientY })
      }
    } else {
      // OFF
      guide.style.display = 'none'
      if (mouseMoveHandler) {
        document.removeEventListener('mousemove', mouseMoveHandler)
        document.removeEventListener('touchmove', handleMove)
        window.removeEventListener('scroll', handleMove)
        mouseMoveHandler = null
      }
    }
  }

  handleReadingGuide.setPreference = level => {
    localStorage.setItem('a11y-reading-guide-level', String(level))
    setReadingGuide(Number(level))
  }
  initButton(button, handleReadingGuide)

  // Initial Load
  const savedLevel = localStorage.getItem('a11y-reading-guide-level')

  // Backward compatibility check (old boolean)
  const savedBool = localStorage.getItem('a11y-reading-guide')
  if (savedLevel !== null) {
    CURRENT_READING_GUIDE_LEVEL = parseInt(savedLevel)
  } else if (savedBool === 'true') {
    CURRENT_READING_GUIDE_LEVEL = 1 // Mapping old true to Line mode
  }

  setReadingGuide(CURRENT_READING_GUIDE_LEVEL)

  // Reset
  registerReset(() => {
    CURRENT_READING_GUIDE_LEVEL = 0
    setReadingGuide(0)
  })
}

let CURRENT_SKIP_NAV_STATE = false

function skipNavigation(
  root,
  initButton,
  toggleFeature,
  registerReset,
  runtime = {},
) {
  const button = {
    name: 'İçeriğe Atla',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
          </svg>`,
    id: 'skipNavigationBtn',
    type: 'button',
  }

  let skipLink = null
  let target = null
  let targetState = null

  function translate(key) {
    return window.a11yI18n ? window.a11yI18n.t(key) : 'Skip to main content'
  }

  function findMainContent() {
    const selectors = [
      '#main-content',
      'main',
      '[role="main"]',
      '.main-content',
      '.page-content',
      '#content',
    ]

    for (const selector of selectors) {
      const element = document.querySelector(selector)
      if (element && !element.closest('[data-accessibility-preference-widget]')) {
        return element
      }
    }
    return null
  }

  function restoreTarget() {
    if (!target || !targetState || !target.isConnected) {
      target = null
      targetState = null
      return
    }

    if (targetState.generatedId) target.removeAttribute('id')
    if (targetState.generatedTabindex) target.removeAttribute('tabindex')
    target = null
    targetState = null
  }

  function updateTarget() {
    if (!CURRENT_SKIP_NAV_STATE || !skipLink) return
    const nextTarget = findMainContent()
    if (nextTarget === target) return

    restoreTarget()
    target = nextTarget
    if (!target) {
      skipLink.removeAttribute('href')
      skipLink.setAttribute('aria-disabled', 'true')
      return
    }

    targetState = {
      generatedId: !target.id,
      generatedTabindex: target.getAttribute('tabindex') === null,
    }
    if (!target.id) target.id = 'a11y-main-content'
    if (targetState.generatedTabindex) target.setAttribute('tabindex', '-1')

    skipLink.href = `#${target.id}`
    skipLink.removeAttribute('aria-disabled')
  }

  function handleSkip(event) {
    event.preventDefault()
    updateTarget()
    if (!target) return

    target.focus({ preventScroll: true })
    target.scrollIntoView({
      behavior:
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      block: 'start',
    })
  }

  function createSkipLink() {
    if (skipLink) return

    skipLink = document.createElement('a')
    skipLink.id = 'a11y-skip-link'
    skipLink.setAttribute('data-a11y-owned', '')
    skipLink.textContent = translate('skipLink')
    skipLink.style.cssText = `
      position: fixed !important;
      inset-block-start: 0 !important;
      inset-inline-start: 1rem !important;
      z-index: 2147483647 !important;
      padding: 0.75rem 1rem !important;
      border: 2px solid currentColor !important;
      border-radius: 0 0 0.5rem 0.5rem !important;
      background: #111827 !important;
      color: #ffffff !important;
      font: 600 1rem/1.25 system-ui, sans-serif !important;
      text-decoration: none !important;
      transform: translateY(-120%) !important;
      transition: transform 0.15s ease !important;
    `
    skipLink.addEventListener('click', handleSkip)
    skipLink.addEventListener('focus', () => {
      skipLink.style.setProperty('transform', 'translateY(0)', 'important')
    })
    skipLink.addEventListener('blur', () => {
      skipLink.style.setProperty('transform', 'translateY(-120%)', 'important')
    })
    document.body.insertBefore(skipLink, document.body.firstChild)
    updateTarget()
  }

  function removeSkipLink() {
    restoreTarget()
    if (skipLink) {
      skipLink.removeEventListener('click', handleSkip)
      skipLink.remove()
      skipLink = null
    }
  }

  function setSkipNavigation(currentState) {
    CURRENT_SKIP_NAV_STATE = Boolean(currentState)
    if (CURRENT_SKIP_NAV_STATE) createSkipLink()
    else removeSkipLink()
    toggleFeature(button, CURRENT_SKIP_NAV_STATE)
  }

  function handleSkipNavigation() {
    const nextState = !CURRENT_SKIP_NAV_STATE
    localStorage.setItem('a11y-skip-nav', String(nextState))
    setSkipNavigation(nextState)
  }

  initButton(button, handleSkipNavigation)

  const previousLanguageUpdater = window.updateSkipLinkText
  const updateSkipLinkText = function () {
    if (skipLink) skipLink.textContent = translate('skipLink')
  }
  window.updateSkipLinkText = updateSkipLinkText

  const savedState = localStorage.getItem('a11y-skip-nav')
  if (savedState !== null) setSkipNavigation(savedState === 'true')

  registerReset(() => setSkipNavigation(false))
  if (runtime.registerRefresh) runtime.registerRefresh(updateTarget)
  if (runtime.registerCleanup) {
    runtime.registerCleanup(() => {
      removeSkipLink()
      if (window.updateSkipLinkText === updateSkipLinkText) {
        if (previousLanguageUpdater) {
          window.updateSkipLinkText = previousLanguageUpdater
        } else {
          delete window.updateSkipLinkText
        }
      }
    })
  }
}

const TEXT_ALIGN_VALUES = ['left', 'center', 'right', 'justify']
let CURRENT_TEXT_ALIGN_LEVEL = 0

function textAlign(root, initButton, cycleFeature, registerReset) {
  const button = {
    name: 'Metin Hizala',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M4 5h16v2H4V5zm0 4h12v2H4V9zm0 4h16v2H4v-2zm0 4h12v2H4v-2z"/>
          </svg>`,
    id: 'textAlignBtn',
    type: 'cycle',
    cycleOptions: {
      maxLevel: 3,
      currentLevel: 0,
    },
  }

  const handleTextAlign = function () {
    CURRENT_TEXT_ALIGN_LEVEL =
      (CURRENT_TEXT_ALIGN_LEVEL + 1) % (button.cycleOptions.maxLevel + 1)
    localStorage.setItem('a11y-text-align', CURRENT_TEXT_ALIGN_LEVEL)
    setTextAlign(CURRENT_TEXT_ALIGN_LEVEL)
  }

  function setTextAlign(currentLevel) {
    const textAlignValue = TEXT_ALIGN_VALUES[currentLevel]

    let styleEl = document.getElementById('a11y-text-align-style')

    // Eğer seviye 0 ise (left - orijinal durum), style elementini kaldır
    if (currentLevel === 0) {
      if (styleEl) {
        styleEl.remove()
      }
      cycleFeature(button, currentLevel)
      return
    }

    // Style elementini oluştur veya güncelle
    if (!styleEl) {
      styleEl = prepareWidgetStyle(document.createElement('style'))
      styleEl.id = 'a11y-text-align-style'
      document.head.appendChild(styleEl)
    }

    styleEl.textContent = `
      body, body *:not(script):not(style):not(#a11y-widget-trigger):not(#a11y-widget-panel):not(#a11y-widget-panel *) {
        text-align: ${textAlignValue} !important;
      }
    `

    cycleFeature(button, currentLevel)
  }

  initButton(button, handleTextAlign)

  function control() {
    let option = localStorage.getItem('a11y-text-align')
    if (option !== null) {
      CURRENT_TEXT_ALIGN_LEVEL = parseInt(option)
      setTextAlign(CURRENT_TEXT_ALIGN_LEVEL)
      // Buton görsel durumunu güncelle
      cycleFeature(button, CURRENT_TEXT_ALIGN_LEVEL)
    }
  }

  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_TEXT_ALIGN_LEVEL = 0
    setTextAlign(0)
  })
}

let CURRENT_TOOLTIP_STATE = false

function tooltip(root, initButton, toggleFeature, registerReset) {
  const button = {
    name: 'İpucu Balonu',
    icon: `<svg class="a11y-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>`,
    id: 'tooltipBtn',
    type: 'button',
  }

  let tooltipElement = null
  let activeElements = []

  const handleTooltip = function () {
    CURRENT_TOOLTIP_STATE = !CURRENT_TOOLTIP_STATE
    localStorage.setItem('a11y-tooltip', CURRENT_TOOLTIP_STATE)
    setTooltip(CURRENT_TOOLTIP_STATE)
  }

  // EN GÖRÜNÜR VERSİYON
  function createTooltip() {
    const tooltip = document.createElement('div')
    tooltip.style.cssText = `
    position: fixed;
    background: #0f766e; /* Daha koyu bir teal */
    color: white;
    padding: 8px 12px;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 700;
    z-index: 100000;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
    max-width: 380px;
    text-align: left;
    line-height: 1.6;
    box-shadow:
      0 12px 50px rgba(0,0,0,0.5),
      0 8px 25px rgba(0,0,0,0.4),
      0 0 0 3px #ffffff;
    border: 3px solid #ffffff;
    word-wrap: break-word;
    white-space: normal;
    text-transform: none;
  `
    return tooltip
  }

  function getSmartPosition(element, tooltipRect) {
    const rect = element.getBoundingClientRect()
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    }

    const positions = []
    const spacing = 10

    // Sağ pozisyon (elementin sağına)
    if (rect.right + tooltipRect.width + spacing <= viewport.width) {
      positions.push({
        x: rect.right + spacing,
        y: rect.top + rect.height / 2 - tooltipRect.height / 2,
        placement: 'right',
      })
    }

    // Sol pozisyon (elementin soluna)
    if (rect.left - tooltipRect.width - spacing >= 0) {
      positions.push({
        x: rect.left - tooltipRect.width - spacing,
        y: rect.top + rect.height / 2 - tooltipRect.height / 2,
        placement: 'left',
      })
    }

    // Alt pozisyon (elementin altına)
    if (rect.bottom + tooltipRect.height + spacing <= viewport.height) {
      positions.push({
        x: rect.left + rect.width / 2 - tooltipRect.width / 2,
        y: rect.bottom + spacing,
        placement: 'bottom',
      })
    }

    // Üst pozisyon (elementin üstüne)
    if (rect.top - tooltipRect.height - spacing >= 0) {
      positions.push({
        x: rect.left + rect.width / 2 - tooltipRect.width / 2,
        y: rect.top - tooltipRect.height - spacing,
        placement: 'top',
      })
    }

    // En iyi pozisyonu seç (mümkünse sağ veya sol)
    const preferredOrder = ['right', 'left', 'bottom', 'top']
    for (const placement of preferredOrder) {
      const position = positions.find(p => p.placement === placement)
      if (position) return position
    }

    // Hiçbiri uygun değilse, viewport içinde sığdırmaya çalış
    return {
      x: Math.max(
        spacing,
        Math.min(viewport.width - tooltipRect.width - spacing, rect.left),
      ),
      y: Math.max(
        spacing,
        Math.min(viewport.height - tooltipRect.height - spacing, rect.top),
      ),
      placement: 'fallback',
    }
  }

  function showTooltip(element, text) {
    if (!tooltipElement) {
      tooltipElement = createTooltip()
      document.body.appendChild(tooltipElement)
    }

    if (!text || text.trim() === '') return

    tooltipElement.textContent = text
    tooltipElement.style.opacity = '0' // Önce gizle

    // Tooltip'i görünür yap ve boyutlarını al
    const tooltipRect = tooltipElement.getBoundingClientRect()

    // Akıllı pozisyon belirleme
    const position = getSmartPosition(element, tooltipRect)

    // Pozisyonu ayarla
    tooltipElement.style.left = `${position.x}px`
    tooltipElement.style.top = `${position.y}px`

    // Görünür yap
    tooltipElement.style.opacity = '1'
  }

  function hideTooltip() {
    if (tooltipElement) {
      tooltipElement.style.opacity = '0'
    }
  }

  // ... getElementInfo fonksiyonu aynı kalacak ...

  function getElementInfo(element) {
    // Butonlar için - sadece görünmeyen açıklamaları göster
    if (
      element.tagName === 'BUTTON' ||
      element.getAttribute('role') === 'button'
    ) {
      const ariaLabel = element.getAttribute('aria-label')
      const title = element.getAttribute('title')
      const ariaDescription = element.getAttribute('aria-description')

      // Sadece görünmeyen açıklamaları göster (buton metnini gösterme)
      return ariaLabel || title || ariaDescription
    }

    // Resimler için
    if (element.tagName === 'IMG') {
      const alt = element.getAttribute('alt')
      const title = element.getAttribute('title')
      return alt || title
    }

    // Linkler için
    if (element.tagName === 'A') {
      const ariaLabel = element.getAttribute('aria-label')
      const title = element.getAttribute('title')

      // Sadece görünmeyen açıklamaları göster
      return ariaLabel || title
    }

    // Inputlar için
    if (
      element.tagName === 'INPUT' ||
      element.tagName === 'TEXTAREA' ||
      element.tagName === 'SELECT'
    ) {
      const placeholder = element.getAttribute('placeholder')
      const ariaLabel = element.getAttribute('aria-label')
      const title = element.getAttribute('title')
      const type = element.getAttribute('type')

      return ariaLabel || title || placeholder || `${type || 'Input'} alanı`
    }

    // Form label'ları için
    if (element.tagName === 'LABEL') {
      const text = element.textContent?.trim()
      const forAttr = element.getAttribute('for')
      if (forAttr) {
        const target = document.getElementById(forAttr)
        if (target) {
          const targetType =
            target.getAttribute('type') || target.tagName.toLowerCase()
          return `${text} (${targetType} alanı)`
        }
      }
      return text
    }

    // İkonlar için (font awesome, material icons vb.)
    if (
      element.classList.contains('fa') ||
      element.classList.contains('fas') ||
      element.classList.contains('far') ||
      element.classList.contains('fab') ||
      element.classList.contains('material-icons') ||
      element.textContent?.trim().length === 1
    ) {
      // Tek karakterli içerik (ikon olabilir)
      const ariaLabel = element.getAttribute('aria-label')
      const title = element.getAttribute('title')
      return ariaLabel || title
    }

    // ARIA landmark'ları için
    if (
      element.getAttribute('role') ||
      element.getAttribute('aria-label') ||
      element.getAttribute('aria-description')
    ) {
      const role = element.getAttribute('role')
      const ariaLabel = element.getAttribute('aria-label')
      const ariaDescription = element.getAttribute('aria-description')
      const title = element.getAttribute('title')

      return ariaLabel || ariaDescription || title || role
    }

    return null
  }

  // ... addTooltipToElement ve diğer fonksiyonlar aynı kalacak ...

  function addTooltipToElement(element) {
    const mouseEnterHandler = () => {
      const info = getElementInfo(element)
      if (info) {
        showTooltip(element, info)
      }
    }

    const mouseLeaveHandler = () => {
      hideTooltip()
    }

    element.addEventListener('mouseenter', mouseEnterHandler)
    element.addEventListener('mouseleave', mouseLeaveHandler)
    element.addEventListener('focus', mouseEnterHandler)
    element.addEventListener('blur', mouseLeaveHandler)

    // Touch events for mobile
    element.addEventListener('touchstart', mouseEnterHandler)
    element.addEventListener('touchend', mouseLeaveHandler)

    activeElements.push({
      element,
      mouseEnterHandler,
      mouseLeaveHandler,
    })
  }

  function setTooltip(currentState) {
    if (currentState) {
      // Tooltip'ı aktif et
      if (!tooltipElement) {
        tooltipElement = createTooltip()
        document.body.appendChild(tooltipElement)
      }

      // Tooltip ekleyeceğimiz elementleri seç
      const elements = document.querySelectorAll(`
        img[alt]:not([alt=""]):not(#a11y-widget-trigger *):not(#a11y-widget-panel *),
        button:not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        a:not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        input:not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        textarea:not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        select:not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        label:not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        [aria-label]:not([aria-label=""]):not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        [title]:not([title=""]):not(#a11y-widget-trigger):not(#a11y-widget-panel *),
        [role]:not(#a11y-widget-trigger):not(#a11y-widget-panel *)
      `)

      elements.forEach(element => {
        // Sadece gerçekten tooltip göstermeye değer elementler
        const info = getElementInfo(element)
        if (info) {
          addTooltipToElement(element)
        }
      })

      // Dinamik olarak eklenen elementler için MutationObserver
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              const elements =
                node.querySelectorAll?.(`
                img[alt]:not([alt=""]),
                button,
                a,
                input,
                textarea,
                select,
                label,
                [aria-label]:not([aria-label=""]),
                [title]:not([title=""]),
                [role]
              `) || []

              elements.forEach(element => {
                if (
                  !element.closest('#a11y-widget-trigger') &&
                  !element.closest('#a11y-widget-panel')
                ) {
                  const info = getElementInfo(element)
                  if (info) {
                    addTooltipToElement(element)
                  }
                }
              })
            }
          })
        })
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })

      window.a11yTooltipObserver = observer
    } else {
      // Tooltip'ı kapat
      if (tooltipElement) {
        tooltipElement.remove()
        tooltipElement = null
      }

      // Event listener'ları temizle
      activeElements.forEach(
        ({ element, mouseEnterHandler, mouseLeaveHandler }) => {
          element.removeEventListener('mouseenter', mouseEnterHandler)
          element.removeEventListener('mouseleave', mouseLeaveHandler)
          element.removeEventListener('focus', mouseEnterHandler)
          element.removeEventListener('blur', mouseLeaveHandler)
          element.removeEventListener('touchstart', mouseEnterHandler)
          element.removeEventListener('touchend', mouseLeaveHandler)
        },
      )

      activeElements = []

      // Observer'ı temizle
      if (window.a11yTooltipObserver) {
        window.a11yTooltipObserver.disconnect()
        delete window.a11yTooltipObserver
      }
    }

    toggleFeature(button, currentState)
  }

  initButton(button, handleTooltip)

  function control() {
    let option = localStorage.getItem('a11y-tooltip')
    if (option !== null) {
      CURRENT_TOOLTIP_STATE = option === 'true'
      setTooltip(CURRENT_TOOLTIP_STATE)
      toggleFeature(button, CURRENT_TOOLTIP_STATE)
    }
  }

  control()

  // Reset fonksiyonunu kaydet
  registerReset(() => {
    CURRENT_TOOLTIP_STATE = false
    setTooltip(false)
  })
}

function main(root, runtime = {}) {
  const panel = root.getElementById('a11y-widget-panel')
  const trigger = root.getElementById('a11y-widget-trigger')
  const closeButton = root.querySelector('.a11y-close')
  const buttonsContainer = root.getElementById('a11y-widget-panel-buttons')
  const resetButton = root.getElementById('a11y-reset-btn')
  const statusRegion = root.getElementById('a11y-widget-status')

  if (!panel || !trigger || !closeButton || !buttonsContainer) {
    throw new Error('Accessibility preference widget markup is incomplete.')
  }

  const moduleHandlers = {}
  const resetRegistry = []
  const refreshRegistry = []
  const cleanupRegistry = []
  const backgroundInertState = new Map()
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')
  let config = runtime.config || {}
  let restoreFocusTo = trigger
  let mutationObserver = null
  let refreshScheduled = false
  let destroyed = false

  function registerReset(resetFunction) {
    if (typeof resetFunction === 'function') resetRegistry.push(resetFunction)
  }

  function registerRefresh(refreshFunction) {
    if (typeof refreshFunction === 'function') refreshRegistry.push(refreshFunction)
  }

  function registerCleanup(cleanupFunction) {
    if (typeof cleanupFunction === 'function') cleanupRegistry.push(cleanupFunction)
  }

  const moduleRuntime = {
    config,
    registerCleanup,
    registerRefresh,
  }

  function getPreferenceSnapshot() {
    const snapshot = new Map()
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index)
        if (key && key.startsWith('a11y-')) {
          snapshot.set(key, localStorage.getItem(key))
        }
      }
    } catch {
      // Storage may be unavailable in hardened or opaque-origin contexts.
    }
    return snapshot
  }

  function restorePreferenceSnapshot(snapshot) {
    try {
      for (const [key, value] of snapshot) {
        if (value !== null) localStorage.setItem(key, value)
      }
    } catch {
      // Runtime cleanup must still succeed when storage is unavailable.
    }
  }

  function runSafely(functions) {
    functions.forEach(callback => {
      try {
        callback()
      } catch {
        // A failed module must not prevent the remaining modules from cleaning up.
      }
    })
  }

  function setBackgroundInert(shouldBeInert) {
    const host = root.host
    if (!host || !document.body) return

    if (shouldBeInert) {
      for (const element of document.body.children) {
        if (element === host) continue
        backgroundInertState.set(element, element.hasAttribute('inert'))
        element.setAttribute('inert', '')
      }
      return
    }

    for (const [element, wasInert] of backgroundInertState) {
      if (!wasInert && element.isConnected) element.removeAttribute('inert')
    }
    backgroundInertState.clear()
  }

  function getFocusableElements() {
    return [...panel.querySelectorAll(focusableSelector)].filter(element => {
      if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false
      return !element.closest('[hidden], [inert]')
    })
  }

  function openPanel() {
    if (destroyed || !panel.hidden) return

    restoreFocusTo = root.activeElement || trigger
    panel.hidden = false
    panel.removeAttribute('inert')
    panel.classList.add('open')
    trigger.setAttribute('aria-expanded', 'true')
    setBackgroundInert(true)

    requestAnimationFrame(() => {
      if (!destroyed && !panel.hidden) closeButton.focus()
    })
  }

  function closePanel({ restoreFocus = true } = {}) {
    if (panel.hidden) {
      setBackgroundInert(false)
      return
    }

    panel.classList.remove('open')
    panel.hidden = true
    panel.setAttribute('inert', '')
    trigger.setAttribute('aria-expanded', 'false')
    setBackgroundInert(false)

    if (
      restoreFocus &&
      restoreFocusTo &&
      restoreFocusTo.isConnected &&
      typeof restoreFocusTo.focus === 'function'
    ) {
      restoreFocusTo.focus()
    }
  }

  function togglePanel() {
    if (panel.hidden) openPanel()
    else closePanel()
  }

  function handlePanelKeydown(event) {
    if (panel.hidden) return

    if (event.key === 'Escape') {
      event.preventDefault()
      closePanel()
      return
    }

    if (event.key !== 'Tab') return

    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) {
      event.preventDefault()
      closeButton.focus()
      return
    }

    const first = focusableElements[0]
    const last = focusableElements[focusableElements.length - 1]
    const activeElement = root.activeElement

    if (event.shiftKey && (activeElement === first || !panel.contains(activeElement))) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function resetPreferences() {
    runSafely(resetRegistry)

    try {
      const keysToRemove = []
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index)
        if (key && key.startsWith('a11y-') && key !== 'a11y-lang') {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch {
      // Visual reset remains useful when persistent storage is unavailable.
    }

    root.querySelectorAll('.brutalist-button').forEach(button => {
      button.classList.remove('active')
      button.setAttribute('aria-pressed', 'false')
      button
        .querySelectorAll('.bar')
        .forEach(progressBar => progressBar.classList.remove('active'))
    })

    if (statusRegion) {
      statusRegion.textContent = window.a11yI18n
        ? window.a11yI18n.t('resetSettings')
        : 'Settings reset'
    }
  }

  function handleDocumentKeydown(event) {
    if (event.key === 'Escape' && !panel.hidden) {
      event.preventDefault()
      closePanel()
    }
  }

  function handleButtonsClick(event) {
    const button = event.target.closest('.brutalist-button')
    if (!button) return

    const handler = moduleHandlers[button.id]
    if (handler) handler()
  }

  function handleDocumentPointerdown(event) {
    if (panel.hidden) return
    const path =
      typeof event.composedPath === 'function' ? event.composedPath() : []
    if (!path.includes(panel) && !path.includes(trigger)) closePanel()
  }

  function attachEventListeners() {
    trigger.addEventListener('click', togglePanel)
    closeButton.addEventListener('click', closePanel)
    if (resetButton) resetButton.addEventListener('click', resetPreferences)
    root.addEventListener('keydown', handlePanelKeydown)
    document.addEventListener('keydown', handleDocumentKeydown)
    buttonsContainer.addEventListener('click', handleButtonsClick)
    document.addEventListener('pointerdown', handleDocumentPointerdown)
  }

  function detachEventListeners() {
    trigger.removeEventListener('click', togglePanel)
    closeButton.removeEventListener('click', closePanel)
    if (resetButton) resetButton.removeEventListener('click', resetPreferences)
    root.removeEventListener('keydown', handlePanelKeydown)
    document.removeEventListener('keydown', handleDocumentKeydown)
    buttonsContainer.removeEventListener('click', handleButtonsClick)
    document.removeEventListener('pointerdown', handleDocumentPointerdown)
  }

  function initButton(button, handler) {
    let progressBars = '<div class="progress-bars" aria-hidden="true">'
    for (let index = 0; index < (button?.cycleOptions?.maxLevel ?? 0); index += 1) {
      progressBars += '<span class="bar"></span>'
    }
    progressBars += '</div>'

    const translatedName = window.a11yI18n
      ? window.a11yI18n.t(button.id)
      : button.name

    const markup = `
      <div class="button-container">
        <span class="a11y-card-check" aria-hidden="true">✓</span>
        <button
          type="button"
          class="brutalist-button button-1"
          id="${button.id}"
          aria-pressed="false"
        >
          <span class="feature-icon" aria-hidden="true">
            ${button.icon}
          </span>
          <span class="button-text">
            <span>${translatedName}</span>
          </span>
          ${button.type === 'cycle' ? progressBars : ''}
        </button>
      </div>
    `

    buttonsContainer.insertAdjacentHTML('beforeend', markup)
    moduleHandlers[button.id] = handler
  }

  function toggleFeature(button, state) {
    const buttonElement = root.getElementById(button.id)
    if (!buttonElement) return

    buttonElement.classList.toggle('active', state)
    buttonElement.setAttribute('aria-pressed', state ? 'true' : 'false')
  }

  function cycleFeature(button, currentLevel) {
    const buttonElement = root.getElementById(button.id)
    if (!buttonElement) return

    buttonElement.querySelectorAll('.progress-bars > .bar').forEach((bar, index) => {
      bar.classList.toggle('active', index < currentLevel)
    })
    buttonElement.classList.toggle('active', currentLevel > 0)
    buttonElement.setAttribute('aria-pressed', currentLevel > 0 ? 'true' : 'false')
    buttonElement.setAttribute('data-level', String(currentLevel))
  }

  function refresh() {
    if (destroyed) return
    runSafely(refreshRegistry)
  }

  function scheduleRefresh() {
    if (refreshScheduled || destroyed) return
    refreshScheduled = true
    const schedule =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : callback => setTimeout(callback, 0)
    schedule(() => {
      refreshScheduled = false
      refresh()
    })
  }

  function startDomObserver() {
    if (
      mutationObserver ||
      config.observeDom === false ||
      typeof MutationObserver !== 'function' ||
      !document.body
    ) {
      return
    }

    mutationObserver = new MutationObserver(mutations => {
      const hasHostMutation = mutations.some(mutation =>
        [...mutation.addedNodes].some(node => {
          if (node.nodeType !== 1) return false
          return node !== root.host
        }),
      )
      if (hasHostMutation) scheduleRefresh()
    })
    mutationObserver.observe(document.body, { childList: true, subtree: true })
  }

  function stopDomObserver() {
    if (mutationObserver) mutationObserver.disconnect()
    mutationObserver = null
  }

  function configure(nextConfig = {}) {
    config = { ...config, ...nextConfig }
    moduleRuntime.config = config
    if (
      nextConfig.language &&
      nextConfig.language !== 'auto' &&
      window.a11yI18n
    ) {
      window.a11yI18n.setLanguage(nextConfig.language, root)
    }
    if (config.observeDom === false) stopDomObserver()
    else startDomObserver()
  }

  function destroy({ preservePreferences = true } = {}) {
    if (destroyed) return
    destroyed = true

    const preferenceSnapshot = preservePreferences
      ? getPreferenceSnapshot()
      : new Map()

    stopDomObserver()
    closePanel({ restoreFocus: false })
    detachEventListeners()
    runSafely(resetRegistry)
    runSafely([...cleanupRegistry].reverse())
    setBackgroundInert(false)
    document
      .querySelectorAll('[data-a11y-owned]')
      .forEach(element => element.remove())

    if (preservePreferences) restorePreferenceSnapshot(preferenceSnapshot)
  }

  if (window.a11yI18n) {
    window.a11yI18n.initLanguage(root)
    const requestedLanguage =
      config.language && config.language !== 'auto'
        ? config.language
        : window.a11yI18n.getCurrentLang()
    window.a11yI18n.setLanguage(requestedLanguage, root)
  }

  attachEventListeners()
  if (typeof languageSelector === 'function') {
    languageSelector(root, moduleRuntime)
  }

  fontSize(root, initButton, cycleFeature, registerReset, moduleRuntime)
  highContrast(root, initButton, cycleFeature, registerReset, moduleRuntime)
  readingGuide(root, initButton, cycleFeature, registerReset, moduleRuntime)
  monochrome(root, initButton, toggleFeature, registerReset, moduleRuntime)
  skipNavigation(root, initButton, toggleFeature, registerReset, moduleRuntime)
  fontFamily(root, initButton, cycleFeature, registerReset, moduleRuntime)
  lineHeight(root, initButton, cycleFeature, registerReset, moduleRuntime)
  pauseAnimations(root, initButton, toggleFeature, registerReset, moduleRuntime)
  hideImages(root, initButton, toggleFeature, registerReset, moduleRuntime)
  highlightLinks(root, initButton, toggleFeature, registerReset, moduleRuntime)
  highlightTitles(root, initButton, toggleFeature, registerReset, moduleRuntime)
  largeCursor(root, initButton, toggleFeature, registerReset, moduleRuntime)
  letterSpacing(root, initButton, cycleFeature, registerReset, moduleRuntime)
  textAlign(root, initButton, cycleFeature, registerReset, moduleRuntime)
  tooltip(root, initButton, toggleFeature, registerReset, moduleRuntime)
  focusIndicator(root, initButton, toggleFeature, registerReset, moduleRuntime)
  audioDescription(root, initButton, toggleFeature, registerReset, moduleRuntime)

  if (typeof profiles === 'function') {
    profiles(root, moduleHandlers, registerReset, moduleRuntime)
  }

  startDomObserver()

  return {
    open: openPanel,
    close: closePanel,
    reset: resetPreferences,
    refresh,
    configure,
    destroy,
  }
}


    controller = main(shadowRoot, {
      config: activeConfig,
      host,
      version: VERSION,
    })
    emit('mounted', { hostId: host.id })
    return host
  }

  function destroy(options = {}) {
    if (!controller && !host) return

    const currentHost = host
    const currentController = controller
    controller = null
    host = null

    if (currentController) {
      currentController.destroy({
        preservePreferences: options.preservePreferences !== false,
      })
    }
    if (currentHost && currentHost.isConnected) currentHost.remove()
    emit('destroyed')
  }

  function refresh() {
    if (!shouldMount()) {
      destroy()
      return
    }
    if (!controller) {
      mount()
      return
    }
    controller.refresh()
    emit('refreshed')
  }

  function open() {
    if (!controller) mount()
    if (controller) controller.open()
  }

  function close() {
    if (controller) controller.close()
  }

  function reset() {
    if (controller) controller.reset()
  }

  function configure(options = {}) {
    activeConfig = normalizeConfig({ ...activeConfig, ...options })
    if (shouldMount()) mount(activeConfig)
    else destroy()
    return { ...activeConfig }
  }

  function scheduleRouteCheck() {
    if (routeCheckScheduled) return
    routeCheckScheduled = true

    const schedule =
      typeof global.requestAnimationFrame === 'function'
        ? global.requestAnimationFrame
        : callback => global.setTimeout(callback, 0)
    const cancel =
      typeof global.cancelAnimationFrame === 'function'
        ? global.cancelAnimationFrame
        : global.clearTimeout

    const handle = schedule(() => {
      cancelScheduledRouteCheck = null
      routeCheckScheduled = false
      const currentPath = getCurrentPath()
      if (currentPath === lastPath) {
        if (controller && activeConfig.observeDom !== false) controller.refresh()
        return
      }
      lastPath = currentPath
      if (shouldMount()) mount()
      else destroy()
      emit('routechange', { path: currentPath })
    })
    cancelScheduledRouteCheck = () => cancel.call(global, handle)
  }

  function startRouteWatcher() {
    global.addEventListener('popstate', scheduleRouteCheck)
    global.addEventListener('hashchange', scheduleRouteCheck)
    document.addEventListener(
      'accessibility-preference-widget:navigate',
      scheduleRouteCheck,
    )

    if (typeof global.MutationObserver === 'function' && document.documentElement) {
      routeObserver = new global.MutationObserver(scheduleRouteCheck)
      routeObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      })
    }
  }

  function disconnect() {
    destroy()
    if (cancelScheduledRouteCheck) cancelScheduledRouteCheck()
    cancelScheduledRouteCheck = null
    routeCheckScheduled = false
    global.removeEventListener('popstate', scheduleRouteCheck)
    global.removeEventListener('hashchange', scheduleRouteCheck)
    document.removeEventListener(
      'accessibility-preference-widget:navigate',
      scheduleRouteCheck,
    )
    if (routeObserver) routeObserver.disconnect()
    routeObserver = null
  }

  const previousApi = global[GLOBAL_NAME]
  if (previousApi && typeof previousApi.disconnect === 'function') {
    previousApi.disconnect()
  }

  activeConfig = normalizeConfig()
  const api = {
    version: VERSION,
    mount,
    destroy,
    refresh,
    open,
    close,
    reset,
    configure,
    disconnect,
    getConfig: () => ({ ...activeConfig }),
    isMounted: () => Boolean(controller && host && host.isConnected),
    getHost: () => host,
  }

  global[GLOBAL_NAME] = api
  startRouteWatcher()

  if (activeConfig.autoMount) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => mount(), { once: true })
    } else {
      mount()
    }
  }
})(window, document)
